/**
 * lib/api.ts — typed fetch wrapper for Meadowlark Platform API v1
 *
 * All requests automatically inject the JWT from localStorage.
 * Base URL is read from VITE_API_URL env var (defaults to empty = same origin).
 */

declare global {
  interface Window { __sessionExpiredShown?: boolean }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ENV_BASE_URL = ((import.meta as any).env?.VITE_API_URL as string) || '';

// Capacitor native (iOS / Android TestFlight) runs from capacitor://localhost — same-origin
// `/api/v1/...` resolves to a non-existent host. Force the production API host at runtime
// so the app works regardless of whether the bundle was built with --mode ios.
const IS_CAPACITOR_NATIVE = (() => {
  try {
    const w = window as unknown as { Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string } };
    if (w?.Capacitor?.isNativePlatform?.()) return true;
    const p = w?.Capacitor?.getPlatform?.();
    return p === 'ios' || p === 'android';
  } catch { return false; }
})();

const BASE_URL = ENV_BASE_URL || (IS_CAPACITOR_NATIVE ? 'https://app.usetenon.com' : '');

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PagedResponse<T> {
  data: T[];
  next_cursor: string;
  has_more: boolean;
}

export interface Project {
  id: string;
  name: string;
  // Status is a tenant-configurable slug (label slugified: lower + spaces→_)
  // sourced from the project_statuses reference list. Free-text by design.
  status: string;
  address: string;
  client_id?: number;
  project_number?: string;
  project_type?: string;
  description?: string;
  value?: number;
  start_date?: string;
  end_date?: string;
  cover_image_url?: string;
  contract_type?: 'fixed_price' | 'cost_plus';
  cost_plus_markup_pct?: number;
  cost_plus_overhead_pct?: number;
  billing_type_locked_at?: string;
  schedule_calendar_id?: number | null;
  location_id?: number | null;
  // TS-P4-GPS-AUTOCLOCKIN: geofence center + radius (meters). All three
  // absent/null = geofence not configured for this project.
  gps_lat?: number | null;
  gps_lng?: number | null;
  gps_radius?: number | null;
  parent_project_id?: number | null;
  parent_project_name?: string | null;
  child_project_count?: number;
  agg_contract_value?: number;
  agg_invoiced_amount?: number;
  agg_costs?: number;
  internal_id?: number;
  created_at: string;
  updated_at: string;
  clients?: ProjectClient[];
}

export interface ProjectClient {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
}

export interface ProjectWithSummary extends Project {
  task_summary: {
    total: number;
    open: number;
    in_progress: number;
    done: number;
  };
}

export interface Task {
  id: string;
  project_id: number;
  title: string;
  description: string;
  status: 'open' | 'in_progress' | 'done' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  type: string;
  assignee_id?: number;
  due_date?: string;
  created_at: string;
  updated_at: string;
}

export interface Comm {
  id: string;
  project_id: number;
  contact_id?: number;
  channel: 'email' | 'slack' | 'buildern' | 'sms' | 'call' | 'note';
  direction: 'in' | 'out';
  subject: string;
  body: string;
  sender_name: string;
  sender_email: string;
  read_at?: string | null;
  metadata?: unknown;
  created_at: string;
  tags: string[];
  ai_classified: boolean;
}

export interface AgentEvent {
  id: string;
  agent_name: string;
  action: string;
  entity_type: string;
  entity_id?: number;
  payload?: unknown;
  status: 'pending' | 'done' | 'failed';
  created_at: string;
}

export interface RFI {
  rfi_number?: string;
  urgency?: "routine" | "urgent" | "critical";
  question?: string;
  formal_response?: string;
  due_date?: string;
  contact_id?: number;
  id: string;
  project_id: number;
  subject: string;
  body: string;
  status: 'draft' | 'open' | 'submitted' | 'sent' | 'in_review' | 'answered' | 'responded' | 'closed';
  ball_in_court: string;
  linked_drawings: string;
  answered_by?: number;
  days_open: number;
  sent_at?: string;
  responded_at?: string;
  created_at: string;
  // RFI Depth fields (M7)
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  assignee_id?: number;
  // Mutually exclusive with assignee_id — a contact with no platform login (#1282).
  contact_assignee_id?: number;
  assignee_name?: string;
  cost_impact?: number;
  schedule_impact_days?: number;
  triggers_co?: boolean;
  co_id?: number;
  created_by?: number;
  title?: string;
}

export interface RFIWatcher {
  id: number;
  name: string;
  email: string;
}

export interface PurchaseOrder {
  id: string;          // UUID (public_id)
  project_id: number;
  supplier_id?: number;
  status: string;      // draft | sent | approved | partially_received | received | shipped | cancelled (DB vocabulary, migration 00900)
  total: number;
  line_items: unknown[];
  created_at: string;
  // Extended fields (returned by enriched list endpoint)
  po_number?: string;
  line_count?: number;
  received_total?: number;
  vendor_name?: string;
  project_name?: string;
  start_date?: string;
  completion_date?: string;
  /** UOW-5D §3.15: the ONE canonical vendor-promised delivery date (YYYY-MM-DD).
   *  PATCH with '' to clear. */
  promised_delivery?: string;
  po_type?: string;
  sc_number?: string;
  scope_of_work?: string;
  language?: string;
  contract_number?: string;
  retention_pct?: number;
}

export interface LoginResponse {
  token: string;
  refresh_token?: string;
  user: { id: number; email: string; name: string; role: string };
}

// ── Token storage ─────────────────────────────────────────────────────────────

// Exported (#1912 MAINT-FE1) so callers that need the raw JWT for a non-header
// reason — decoding the `uid` claim, appending ?session_token= to a media URL a
// browser fetches without our headers — have one accessor to reach for instead
// of hand-rolling another localStorage.getItem('ml_token'). Requests themselves
// should use authedFetch/request, which inject the header for you.
// ── Portal-preview per-tab isolation (#2151) ─────────────────────────────────
// A client-portal PREVIEW opens in a NEW TAB via window.open(..., '_blank')
// from the admin's settings, so it SHARES the admin tab's localStorage. Writing
// the preview's client JWT to the shared `ml_token` key silently re-authed the
// admin's ORIGINAL tab as the previewed client (SSE reconnect + every apiGet),
// and a preview-side clearToken()/refresh would wipe or rotate the admin's real
// session. Fix: the preview tab keeps its identity in sessionStorage — tab-
// scoped (window.open gets its own sessionStorage, and sessionStorage writes
// never fire cross-tab 'storage' events). These two helpers are the single
// place that knows about it, so no other call site has to.
export function isPreviewTab(): boolean {
  try { return sessionStorage.getItem('ml_preview_mode') === '1'; } catch { return false; }
}

// The active token for THIS tab: the preview tab's sessionStorage JWT if
// present, else the durable localStorage token. Normal tabs have no
// sessionStorage ml_token, so this is identical to reading localStorage.
// Page-scoped auth fallback for surfaces that run WITHOUT a login — today
// only the Shop Kiosk (#3597). The kiosk page sets its kiosk token here on
// mount and clears it on unmount, so shared data hooks (useRefList,
// useTaskPeekDependencies, any apiGet caller it composes) authenticate with
// the same token the page's own authFetch uses instead of 401ing. A real
// session token, when present, always wins — the fallback only fills the
// no-login gap, and it never leaks past the page that set it.
let fallbackToken: string | null = null;
export function setFallbackToken(token: string | null): void {
  fallbackToken = token;
}

export function getActiveToken(): string | null {
  try {
    const scoped = sessionStorage.getItem('ml_token');
    if (scoped) return scoped;
  } catch { /* sessionStorage unavailable — fall through to localStorage */ }
  return localStorage.getItem('ml_token') ?? fallbackToken;
}

export function getToken(): string | null {
  return getActiveToken();
}

export function isAccessTokenUnexpired(token: string | null, nowSeconds = Math.floor(Date.now() / 1000)): boolean {
  if (!token) return false;
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(payload)) as { exp?: number };
    return typeof claims.exp === 'number' && claims.exp > nowSeconds;
  } catch {
    return false;
  }
}

// Falls back to durable Preferences on native when localStorage came up empty
// (WKWebView eviction on background/kill) — belt-and-suspenders so a merely-
// evicted localStorage never fails a 401 refresh outright (#1096 AUTH-MOBILE-01).
export async function getRefreshToken(): Promise<string | null> {
  const local = localStorage.getItem('ml_refresh_token');
  if (local) return local;
  try {
    const w = window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } };
    if (!w?.Capacitor?.isNativePlatform?.()) return null;
    const m = await import('@/lib/native');
    const { value } = await m.getPreference('ml_refresh_token');
    return value || null;
  } catch {
    return null;
  }
}

// setToken/setRefreshToken await the native Preferences write before
// returning (#1096 AUTH-MOBILE-01) — WKWebView localStorage isn't durable
// across app background/kill, so a fire-and-forget mirror left a window
// where a 401 refresh completed but the app got killed before the durable
// copy landed, silently dropping the refresh token and forcing a fresh
// login (2FA) every ~15 min on native. On web this is a no-op resolve.
export async function setToken(token: string, opts?: { skipSideEffects?: boolean }): Promise<void> {
  localStorage.setItem('ml_token', token);
  // A real sign-in clears the intentional-signout guard (#3082) so future
  // resumes work normally.
  try { localStorage.removeItem('intentional_signout'); } catch { /* ignore */ }
  // Decode and store expiry for proactive-refresh logic
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp) {
      localStorage.setItem('ml_token_exp', String(payload.exp));
    }
  } catch (_) { /* ignore decode errors */ }
  // Impersonation swaps pass skipSideEffects (#2091): a borrowed token must
  // NOT reach the paired Apple Watch or the durable native mirror, and SSE
  // needs no explicit connect — both impersonation paths hard-reload, and the
  // App bootstrap reconnects SSE with whatever ml_token is active. Storing
  // ml_token_exp above is deliberately NOT skipped: it describes the active
  // token, and leaving the previous token's exp in place made the proactive
  // refresh fire with the admin's refresh token minutes into an impersonation,
  // silently swapping identity back to the admin.
  if (opts?.skipSideEffects) return;
  // Push credentials to paired Apple Watch via WatchConnectivity (iOS native only)
  _syncWatchToken(token);
  // Mirror auth into durable native storage so it survives app updates.
  await _mirrorAuth();
  // Open/refresh the realtime event stream (chat, notifications, Sage
  // streaming, typing indicators all subscribe via useSSEEvent but nothing
  // was ever calling connect() — see hooks/useSSE.ts).
  await _connectSSE(token);
}

export async function setRefreshToken(refreshToken: string): Promise<void> {
  localStorage.setItem('ml_refresh_token', refreshToken);
  // Sync refresh token to biometric storage (so biometric-stored token stays current)
  try {
    const w = window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } };
    if (w?.Capacitor?.isNativePlatform?.()) {
      import('@/lib/native').then(m => m.updateBiometricRefreshToken(refreshToken)).catch(() => {});
    }
  } catch { /* not native */ }
  await _mirrorAuth();
}

export async function refreshTokenWithRefreshToken(refreshToken: string): Promise<boolean> {
  return _performTokenRefresh(refreshToken);
}

export function clearToken(): void {
  // Preview tab (#2151): clear ONLY this tab's sessionStorage session. Touching
  // the shared localStorage token here (e.g. on a preview-side 401) would fire a
  // 'storage' event that disconnects SSE and logs out the admin's original tab.
  if (isPreviewTab()) {
    try {
      sessionStorage.removeItem('ml_token');
      sessionStorage.removeItem('ml_token_exp');
      sessionStorage.removeItem('ml_preview_token');
      sessionStorage.removeItem('ml_preview_mode');
      sessionStorage.removeItem('ml_preview_name');
      sessionStorage.removeItem('ml_user');
    } catch { /* ignore */ }
    return;
  }
  localStorage.removeItem('ml_token');
  localStorage.removeItem('ml_token_exp');
  localStorage.removeItem('ml_refresh_token');
  // Sign-out propagation to Apple Watch
  _syncWatchToken('');
  // Clear the durable mirror too so a stale token can't be restored after sign-out.
  _mirrorAuth();
  _disconnectSSE();
}

// Durable, awaited sign-out (#3082). clearToken() clears localStorage
// synchronously but mirrors the durable Capacitor Preferences copy only
// fire-and-forget — so on native the login page's auto-resume could read the
// still-present refresh token and sign the user right back in. signOut():
//   1. sets an intentional-signout guard that resumeStoredSession() +
//      restoreAuthFromPreferences() honour, closing the race even if the async
//      clear is slow;
//   2. clears the fast (localStorage) copy;
//   3. AWAITS removal of the durable Preferences + biometric copies.
// Sign-out handlers should `await` this before navigating to /login.
export async function signOut(): Promise<void> {
  try { localStorage.setItem('intentional_signout', '1'); } catch { /* ignore */ }
  clearToken();
  try {
    const w = window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } };
    if (w?.Capacitor?.isNativePlatform?.()) {
      const m = await import('@/lib/native');
      await m.clearAuthPreferences();
    }
  } catch { /* best effort — localStorage is already cleared */ }
}

// Dynamic import avoids a static circular dependency (hooks/useSSE.ts imports
// apiBaseUrl from this module).
async function _connectSSE(token: string): Promise<void> {
  try {
    const { sseManager } = await import('@/hooks/useSSE');
    sseManager.connect(token);
  } catch { /* non-fatal — realtime updates degrade to manual refresh */ }
}

function _disconnectSSE(): void {
  import('@/hooks/useSSE').then(({ sseManager }) => sseManager.disconnect()).catch(() => {});
}

// Mirror the localStorage auth keys (token, refresh, trusted-device) into
// Capacitor Preferences, which survives iOS app updates. Dynamic import keeps
// the native plugin out of the web bundle. No-op off-native.
function _mirrorAuth(): void {
  try {
    const w = window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } };
    if (!w?.Capacitor?.isNativePlatform?.()) return;
    import('@/lib/native').then(m => m.mirrorAuthToPreferences()).catch(() => {});
  } catch { /* not native */ }
}

function _syncWatchToken(token: string): void {
  try {
    const w = window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } };
    if (!w?.Capacitor?.isNativePlatform?.()) return;
    // Dynamic import avoids bundling Capacitor plugin registration on web
    import('@capacitor/core').then(({ registerPlugin }) => {
      const WatchAuth = registerPlugin<{
        syncToken(opts: { token: string; baseUrl: string; voice: string }): Promise<void>;
      }>('WatchAuth');
      const voice = localStorage.getItem('sage_voice_pref_v1') || 'af_heart';
      WatchAuth.syncToken({ token, baseUrl: 'https://app.usetenon.com', voice }).catch(() => {});
    }).catch(() => {});
  } catch { /* not native */ }
}

// Re-push the currently-stored token into UserDefaults.standard (which CarPlay
// + Apple Watch read) on app boot. Without this, an already-logged-in user who
// updates the app never calls setToken() again, so the auxiliary surfaces see a
// stale/missing token and show "Sign in on iPhone" with empty Today/Projects/
// Recent/Sage — even though the phone IS signed in. Call this once at startup
// and on resume. No-op off-native and when signed out.
export function syncAuxTokenFromStorage(): void {
  const token = localStorage.getItem('ml_token');
  if (token) _syncWatchToken(token);
}

// ── Proactive refresh (fires silently in background after each API call) ───────
//
// With 15-min access JWTs, trigger a background refresh when less than 60 s
// remain. Uses the refresh_token (DB-backed rotation) when available; falls
// back to the legacy Bearer self-refresh for old 24 h sessions.

let _proactiveRefreshInFlight = false;
async function maybeRefreshToken(): Promise<void> {
  // Preview tab (#2151): uses an ephemeral client JWT and shares the admin's
  // localStorage refresh token — never spend/rotate it from a preview tab.
  if (isPreviewTab()) return;
  const expStr = localStorage.getItem('ml_token_exp');
  if (!expStr) return;
  const exp = parseInt(expStr, 10);
  const secondsLeft = exp - Math.floor(Date.now() / 1000);
  if (secondsLeft > 60) return;  // plenty of time — nothing to do
  if (secondsLeft <= 0) return;  // already expired — let 401 interceptor handle

  const refreshToken = await getRefreshToken();
  if (refreshToken) {
    // Token-pair path (new sessions). CRITICAL: route through the SAME
    // single-flight promise as the 401 reactive path (coalesceRefresh). The
    // refresh token rotates on every use, and the backend revokes the whole
    // token family if a rotated/old token is re-presented (compromise
    // detection) — forcing logout on all devices. If the proactive refresh and
    // a reactive 401 refresh each fired their own fetch with the same token,
    // one would rotate it and the other would re-present the dead token →
    // family revoked → "constantly logged out on mobile". Coalescing both onto
    // one in-flight refresh guarantees a token is spent exactly once.
    await coalesceRefresh();
    return;
  }

  // Legacy path: still-valid Bearer (old 24h web sessions, no refresh token).
  if (_proactiveRefreshInFlight) return;
  _proactiveRefreshInFlight = true;
  try {
    const token = getToken();
    if (!token) return;
    const res = await fetch(`${BASE_URL}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const data = await res.json() as { token: string };
      await setToken(data.token);
    }
  } catch (_) { /* silent */ } finally {
    _proactiveRefreshInFlight = false;
  }
}

// ── Silent refresh on 401 (coalesces concurrent requests + cross-tab) ─────────

let _refreshOnExpiry: Promise<boolean> | null = null;

async function _doTokenRefresh(): Promise<boolean> {
  // Multi-tab coordination: use Web Locks API to ensure only one tab refreshes at a time.
  // If the browser supports it, acquire a shared lock named 'tenon-token-refresh' and hold
  // it while fetching. Read the rotating token only AFTER acquiring the lock:
  // another tab may replace it while this tab waits. Submitting the pre-lock
  // value triggers reuse detection and revokes the whole device family.
  if (navigator.locks) {
    try {
      return await navigator.locks.request('tenon-token-refresh', async () => {
        // localStorage is shared across tabs. If the winning tab already wrote
        // a fresh access token, this tab has nothing left to rotate.
        if (isAccessTokenUnexpired(getActiveToken())) return true;
        const currentRefreshToken = await getRefreshToken();
        if (!currentRefreshToken) return false;
        return await _performTokenRefresh(currentRefreshToken);
      });
    } catch {
      // Locks API not supported or failed; fall back to single-tab coalescing
      const currentRefreshToken = await getRefreshToken();
      return currentRefreshToken ? await _performTokenRefresh(currentRefreshToken) : false;
    }
  }

  const refreshToken = await getRefreshToken();
  return refreshToken ? await _performTokenRefresh(refreshToken) : false;
}

async function _performTokenRefresh(refreshToken: string): Promise<boolean> {
  try {
    const res = await fetch(`${BASE_URL}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return false;
    const data = await res.json() as { token: string; refresh_token?: string };
    await setToken(data.token);
    if (data.refresh_token) await setRefreshToken(data.refresh_token);
    return true;
  } catch {
    return false;
  }
}

// Coalesce all concurrent 401s onto a single in-flight refresh request.
function coalesceRefresh(): Promise<boolean> {
  // Preview tab (#2151): never rotate the admin's shared refresh token — a
  // preview-side 401 falls through to clearToken()'s tab-local path instead.
  if (isPreviewTab()) return Promise.resolve(false);
  if (!_refreshOnExpiry) {
    _refreshOnExpiry = _doTokenRefresh().finally(() => { _refreshOnExpiry = null; });
  }
  return _refreshOnExpiry;
}

// Resolve a persisted device session before routing through the sign-in page.
// A live access JWT bypasses immediately. An expired/missing access JWT with a
// valid persisted refresh token rotates silently and remains on this device.
export async function resumeStoredSession(): Promise<boolean> {
  if (isPreviewTab()) return isAccessTokenUnexpired(getActiveToken());
  // Just signed out on purpose (#3082): do not auto-resume from the durable
  // refresh token. Cleared by setToken() on the next real sign-in.
  if (localStorage.getItem('intentional_signout') === '1') return false;
  if (isAccessTokenUnexpired(localStorage.getItem('ml_token'))) return true;
  if (!await getRefreshToken()) return false;
  return coalesceRefresh();
}

// ── Core request function ─────────────────────────────────────────────────────

// ApiError carries the HTTP status + parsed JSON body alongside the message,
// so callers that need to react to a specific status (e.g. 409 conflict on
// the app_tasks version guard, #801) don't have to re-parse res.text().
// Extends Error, so every existing `catch (e) { (e as Error).message }`
// caller keeps working unchanged.
export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

async function request<T>(path: string, options: RequestInit = {}, _isRetry = false): Promise<T> {
  const token = getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers as Record<string, string> || {}),
    },
  });

  if (res.status === 204) return undefined as unknown as T;

  // Parse body as text first so we can extract error messages even from non-JSON responses
  const text = await res.text();
  let json: unknown = null;
  try { json = JSON.parse(text); } catch { /* not JSON */ }

  if (res.status === 402) {
    const code = (json as { code?: string } | null)?.code;
    if (code === "trial_expired" || code === "subscription_inactive") {
      window.location.href = "/billing";
    }
    throw new Error((json as { message?: string } | null)?.message || "Payment required");
  }

  if (res.status === 401) {
    // If the server signals token_expired (not a hard auth failure), attempt
    // a silent refresh and transparently retry the original request once.
    // (#3106: the native app can now read this header because the API exposes
    // it via Access-Control-Expose-Headers; the warm-resume proactive refresh
    // in App.jsx also tops the token up before requests fire.)
    const wwwAuth = res.headers.get('WWW-Authenticate') || '';
    if (!_isRetry && wwwAuth.includes('token_expired')) {
      const refreshed = await coalesceRefresh();
      if (refreshed) {
        return request<T>(path, options, true /* _isRetry */);
      }
    }
    // Offline → a transient network failure is not a revoked session. Keep it
    // and let the next online request retry rather than yanking a field user on
    // bad signal to /login (the "logged out on bad signal" class, #3106).
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      throw new Error("Network unavailable");
    }
    clearToken();
    if (!window.__sessionExpiredShown) {
      window.__sessionExpiredShown = true;
      sessionStorage.setItem('session_expired', '1');
      window.location.href = '/login';
    }
    throw new Error("Session expired");
  }
  if (!res.ok) {
    const errMsg = (json as { error?: string; message?: string } | null)?.error
      || (json as { error?: string; message?: string } | null)?.message
      || text
      || `HTTP ${res.status}`;
    throw new ApiError(errMsg, res.status, json);
  }
  // Fire-and-forget proactive refresh check
  maybeRefreshToken().catch(() => { /* silent */ });
  return json as T;
}

// ── authedFetch — raw-Response sibling of request() (#1912 MAINT-FE1) ─────────

// The codebase has ~739 bare `fetch('/api...')` calls that hand-roll their own
// `Authorization: Bearer ${localStorage.getItem('ml_token')}` header. Exactly
// one of them checks for a 401. That means every one of them:
//   • misses the silent refresh+retry below, so a merely-expired access token
//     surfaces as a spurious request failure (the #1 "logged out on mobile"
//     incident class) instead of being transparently retried;
//   • reads the token at call time from localStorage, so a request issued
//     mid-rotation sends a token that coalesceRefresh() has already spent;
//   • uses a relative `/api/...` path, which 404s under Capacitor native where
//     the webview origin isn't the API origin (BASE_URL exists for this).
//
// authedFetch fixes all three while returning a raw Response, so callers that
// can't use request<T>() — FormData uploads, blob/arrayBuffer downloads, and
// anything that inspects headers or a non-JSON body — can migrate too.
//
// Deliberately NOT the same as request<T>():
//   • No default Content-Type. request<T>() forces application/json; doing that
//     here would clobber the multipart boundary the browser generates for
//     FormData, which is precisely the caller class this wrapper exists to
//     serve. Callers sending JSON set the header themselves (or use request).
//   • Never parses the body and never throws on a non-2xx. The caller owns the
//     Response, so `res.ok` / `res.status` checks keep working unchanged.
export interface AuthedFetchOptions {
  // On a 401 that silent refresh could not rescue, request<T>() clears the
  // token and hard-redirects to /login. authedFetch does the same by default so
  // migrated callers behave identically to their api.ts siblings. Background
  // pollers and SSE reconnect probes should pass false: for them a transient
  // hard 401 should surface as a plain Response, not yank the whole tab to the
  // login screen out from under work in progress.
  redirectOnAuthFailure?: boolean;
}

export async function authedFetch(
  path: string,
  init: RequestInit = {},
  opts: AuthedFetchOptions = {},
): Promise<Response> {
  const { redirectOnAuthFailure = true } = opts;
  return _authedFetch(path, init, redirectOnAuthFailure, false);
}

async function _authedFetch(
  path: string,
  init: RequestInit,
  redirectOnAuthFailure: boolean,
  _isRetry: boolean,
): Promise<Response> {
  // Absolute URLs pass through untouched — some callers hit S3/presigned or
  // third-party endpoints and must not be rewritten onto the API origin.
  const url = /^https?:\/\//i.test(path) ? path : `${BASE_URL}${path}`;

  // `new Headers()` normalises all three RequestInit header shapes (Headers
  // instance, [k,v][] tuples, plain record). request<T>() casts straight to
  // Record, which silently drops a caller-supplied Headers instance — callers
  // migrating off bare fetch use all three shapes, so normalise properly here.
  const headers = new Headers(init.headers);
  const token = getToken();
  // Caller-supplied Authorization wins (e.g. the portal's client-token calls).
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const res = await fetch(url, { ...init, headers });

  if (res.status === 401) {
    // Same contract as request<T>(): only a server-signalled token_expired is
    // recoverable. A hard 401 (bad credentials, revoked family) must not be
    // retried — re-presenting a dead refresh token is what trips the backend's
    // token-family compromise detection and logs the user out everywhere.
    const wwwAuth = res.headers.get('WWW-Authenticate') || '';
    if (!_isRetry && wwwAuth.includes('token_expired')) {
      const refreshed = await coalesceRefresh();
      if (refreshed) {
        return _authedFetch(path, init, redirectOnAuthFailure, true /* _isRetry */);
      }
    }
    // Offline → transient failure, not a revoked session: keep it and surface
    // the raw 401 without logging out (#3106).
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return res;
    }
    if (redirectOnAuthFailure) {
      clearToken();
      if (!window.__sessionExpiredShown) {
        window.__sessionExpiredShown = true;
        sessionStorage.setItem('session_expired', '1');
        window.location.href = '/login';
      }
    }
    return res;
  }

  if (res.ok) {
    // Fire-and-forget proactive refresh, matching request<T>(). Without this,
    // a page driven entirely by authedFetch would never top up its access
    // token and would rely on the reactive 401 path on every expiry.
    maybeRefreshToken().catch(() => { /* silent */ });
  }
  return res;
}

function body(data: unknown): RequestInit {
  return { body: JSON.stringify(data) };
}

// ── Auth ──────────────────────────────────────────────────────────────────────

// Convenience wrappers
const get = <T>(path: string) => request<T>(`/api/v1${path}`);
const post = <T>(path: string, data?: unknown) => request<T>(`/api/v1${path}`, { method: "POST", ...(data !== undefined ? body(data) : {}) });
const patch = <T>(path: string, data: unknown) => request<T>(`/api/v1${path}`, { method: "PATCH", ...body(data) });
const del = <T = void>(path: string) => request<T>(`/api/v1${path}`, { method: "DELETE" });

export const auth = {
  login: async (email: string, password: string, rememberMe?: boolean): Promise<LoginResponse> => {
    // #646: send the remembered-device token so a trusted device skips MFA.
    const td = localStorage.getItem('tenon_trusted_device');
    const headers: Record<string, string> = td ? { 'X-Tenon-Trusted-Device': td } : {};
    const data = await request<LoginResponse>('/api/v1/auth/login', { method: 'POST', ...body({ email, password, remember_me: rememberMe ?? false }), headers });
    if (data.refresh_token) await setRefreshToken(data.refresh_token);
    return data;
  },
  register: async (companyName: string, name: string, email: string, password: string): Promise<LoginResponse> => {
    const data = await request<LoginResponse>('/api/v1/auth/register', { method: 'POST', ...body({ company_name: companyName, name, email, password }) });
    if (data.refresh_token) await setRefreshToken(data.refresh_token);
    return data;
  },
  // Explicit refresh using the stored refresh_token (or legacy Bearer self-refresh)
  refresh: async (): Promise<LoginResponse> => {
    const refreshToken = await getRefreshToken();
    if (refreshToken) {
      const data = await request<LoginResponse>('/api/v1/auth/refresh', { method: 'POST', ...body({ refresh_token: refreshToken }) });
      if (data.refresh_token) await setRefreshToken(data.refresh_token);
      return data;
    }
    return request<LoginResponse>('/api/v1/auth/refresh', { method: 'POST' });
  },
  revoke: (opts?: { device_id?: string; family_id?: string; all?: boolean }) =>
    request<{ revoked: number }>('/api/v1/auth/revoke', { method: 'POST', ...body(opts ?? { all: true }) }),
  me: () => request<LoginResponse['user']>('/api/v1/auth/me'),
  /**
   * POST /api/v1/auth/apple/native — called by LoginPage after the native
   * Capacitor SIWA plugin returns. Pass identity_token + optional name.
   * Backend verifies the JWT, then either logs in or returns redirect:signup.
   *
   * Nonce CSRF guard intentionally omitted on the client for now — build 41
   * with the plugin-side nonce flow regressed the native sheet (Joe report
   * 2026-06-02). Backend retains a skip-when-empty check so we can reintroduce
   * the nonce later without API breakage.
   */
  appleNative: (
    identityToken: string,
    user?: { firstName?: string; lastName?: string },
  ) =>
    post<{ token?: string; refresh_token?: string; next?: string; redirect?: string; email?: string; name?: string }>(
      '/auth/apple/native',
      { identity_token: identityToken, user }
    ),
  /**
   * POST /api/v1/auth/native/exchange — AUTH-02 (#728). Trades the one-time
   * code from the OAuth deep-link for a token pair, proving possession of the
   * PKCE verifier this app generated before opening the browser.
   *
   * Replaces the old flow where the callback URL carried the raw access +
   * refresh token and any app registering `com.usetenon.app://` could steal it.
   */
  nativeExchange: (code: string, codeVerifier: string) =>
    post<{ token: string; refresh_token: string; next?: string }>(
      '/auth/native/exchange',
      { code, code_verifier: codeVerifier }
    ),
};

// ── Projects ──────────────────────────────────────────────────────────────────

export const projects = {
  list: (params?: { status?: string; search?: string; limit?: number; cursor?: string; aggregate_children?: boolean }) => {
    const q = params ? new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([,v]) => v !== undefined && v !== false).map(([k,v]) => [k, String(v)])) as Record<string, string>).toString() : '';
    return request<PagedResponse<Project>>(`/api/v1/projects${q ? '?' + q : ''}`);
  },
  get: (id: string) => request<ProjectWithSummary>(`/api/v1/projects/${id}`),
  create: (data: Partial<Project>) =>
    request<Project>('/api/v1/projects', { method: 'POST', ...body(data) }),
  update: (id: string, data: Partial<Project>) =>
    request<Project>(`/api/v1/projects/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: string) => request<void>(`/api/v1/projects/${id}`, { method: 'DELETE' }),
  uploadCoverImage: async (id: string, file: File): Promise<{ cover_image_url: string }> => {
    const token = getToken();
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`/api/v1/projects/${id}/cover-image`, {
      method: 'POST',
      headers: token ? { Authorization: 'Bearer ' + token } : {},
      body: form,
    });
    if (!res.ok) {
      // Surface the server's error message (file-too-large, bad MIME, etc.)
      // so the toast tells the user what's actually wrong.
      let msg = 'Cover image upload failed';
      try {
        const body = await res.json();
        msg = body?.error || body?.message || msg;
      } catch { /* non-JSON error body */ }
      throw new Error(msg);
    }
    return res.json();
  },
  listRocks: (id: string | number) => request<ProjectRock[]>(`/api/v1/projects/${id}/rocks`),
};

// ── AppTask (v2 Tenon task system) ───────────────────────────────────────────

export type TaskPriority = 'Urgent' | 'High' | 'Normal' | 'Low';
export type TaskStatus = 'Not Started' | 'In Progress' | 'Blocked' | 'Completed' | 'Cancelled';
export type TaskType = 'Custom Build' | 'Callback' | 'Closeout' | 'Office' | 'Maintenance' | 'Field';
export type ScheduleType = 'task' | 'phase' | 'milestone' | 'decision';

export interface AppTask {
  id: string;
  company_id: number;
  name: string;
  project_id?: number;
  priority: TaskPriority;
  status: TaskStatus;
  type: TaskType;
  assignee_id?: number;
  // Multi-assignee
  assignee_ids?: number[];
  contact_assignee_ids?: number[];
  assignee_names?: string[];
  contact_assignee_names?: string[];
  goal_id?: number;
  notes?: string;
  due_date?: string;
  start_date?: string;
  end_date?: string;
  duration_minutes?: number;
  is_shop_task: boolean;
  /** Task-level "internally billed" flag — surfaces on the Internal section.
   *  A task may have a customer project_id and still be flagged internal. */
  is_internal_billing?: boolean;
  repeat_type?: 'date' | 'completion';
  repeat_frequency?: string;
  repeat_value?: number;
  next_occurrence?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  // Location + entity polymorphism (sprint 39)
  location_id?: number;
  entity_type?: string;
  entity_id?: number;
  /** Per-task inspection outcome — only meaningful when entity_type='permit'.
   *  Values: pending | passed | failed | conditional. Set via PATCH. */
  inspection_outcome?: string;
  // Subtask support
  parent_task_id?: string;
  subtask_count?: number;
  completion_percent?: number;
  // Schedule item association
  schedule_item_id?: number;
  schedule_item_title?: string;
  // Estimate line item association (#2936) — ties this task to the
  // estimate line that justifies the work.
  estimate_line_item_id?: number | null;
  // Hydrated join fields
  /** Project public_id (UUID) joined from projects. Needed for UUID-aware
   *  client-side filters (task forms filter phases by project UUID). */
  project_public_id?: string;
  project_name?: string;
  assignee_name?: string;
  goal_name?: string;
  location_name?: string;
  // D7 task redesign fields
  trade_category?: string;
  template_id?: number;
  completion_notes?: string;
  labels?: string[];
  // Phase system (Sprint B2)
  schedule_type?: ScheduleType;
  phase_id?: string;
  gantt_order?: number;
  subtask_sort_order?: number;
  // Set when schedule_type='decision' — UUID of the linked decisions row
  linked_decision_id?: string;
  // Materials readiness — auto-derived by the backend from task_materials + POs.
  // Values: null (ungroomed) | 'none' | 'ready' | 'waiting' | 'required' | 'sourcing'
  material_status?: string | null;
  // FS room assignment — UUID of the decision_folders row.
  room_folder_id?: string | null;
  room_folder_name?: string | null;
  // Current open flag (#802/#810), embedded directly by GET /tasks/{id} —
  // null when the task isn't flagged. See TaskFlag below.
  flag?: TaskFlag | null;
}

// ── TaskFlag — mirrors internal/api/task_flags_handlers.go's TaskFlag (#802) ──
// One row per flag/resolve cycle; the "current" flag is the latest
// unresolved one, which is what GET /tasks/{id} embeds and what
// GET /tasks/{id}/flag returns.
export interface TaskFlag {
  id: number;
  company_id: number;
  task_id: string;
  reason: string;
  note: string;
  photo_attachment_ids: number[];
  notify_user_ids: number[];
  flagged_by?: number;
  flagged_by_name?: string;
  flagged_at: string;
  resolved_by?: number;
  resolved_at?: string | null;
  resolution_note?: string;
}

// ── AppDecision — mirrors the decisions table row returned by GET /api/v1/decisions/{id} ─────────

export interface AppDecisionOption {
  id: string;
  decision_id: string;
  title: string;
  description_md: string;
  is_baseline: boolean;
  price?: number;
  quantity?: number;
  unit?: string;
  brand?: string;
  sku?: string;
  product_url?: string;
  cost_catalogue_item_id?: number;
  tax_pct?: number;
  is_taxable: boolean;
  revision: number;
  archived_at?: string;
  proposed_by_client: boolean;
  sort_order: number;
  selected_at?: string;
  selected_by_user_id?: number;
  created_at: string;
  updated_at: string;
  image_url?: string;
}

export interface AppDecision {
  schedule_type: 'decision';
  id: string;
  company_id: number;
  project_id: number;
  folder_id?: string;
  kind: string;
  title: string;
  description_md: string;
  selection_mode: string;
  close_mode: string;
  deadline?: string;
  has_pricing: boolean;
  status: string;
  current_revision: number;
  sent_to_client_at?: string;
  closed_at?: string;
  target_date?: string;
  phase_id?: string;
  spec_type?: string;
  allowance?: number;
  options?: AppDecisionOption[];
  created_at: string;
  updated_at: string;
}

// ── AppMilestone — app_task row with schedule_type='milestone' ────────────────────────────────

export interface AppMilestone {
  schedule_type: 'milestone';
  id: string;
  company_id: number;
  name: string;
  project_id?: number;
  project_name?: string;
  project_public_id?: string;
  status: TaskStatus;
  priority: TaskPriority;
  assignee_id?: number;
  assignee_name?: string;
  due_date?: string;
  start_date?: string;
  end_date?: string;
  phase_id?: string;
  gantt_order?: number;
  notes?: string;
  labels?: string[];
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

// ── AppPhase — app_task row with schedule_type='phase' ───────────────────────────────────────

export interface AppPhase {
  schedule_type: 'phase';
  id: string;
  company_id: number;
  name: string;
  project_id?: number;
  project_name?: string;
  project_public_id?: string;
  phase_id?: string;
  gantt_order?: number;
  start_date?: string;
  end_date?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

// ── Discriminated union for schedule items ───────────────────────────────────────────────────

export type TaskOrDecisionOrMilestoneOrPhase =
  | (AppTask & { schedule_type: 'task' })
  | AppDecision
  | AppMilestone
  | AppPhase;

export interface AppGoal {
  id: string;
  company_id: number;
  name: string;
  description: string;
  status: string;
  priority?: 'low' | 'medium' | 'high';
  rock_status?: 'on_track' | 'at_risk' | 'off_track';
  owner_id?: number;
  owner_name?: string;
  cycle_id?: number;
  cycle_name?: string;
  parent_goal_id?: number;
  parent_goal_name?: string;
  project_id?: number;
  project_name?: string;
  due_date?: string;
  created_at: string;
  updated_at: string;
}

export interface GoalComment {
  id: string;
  goal_id: number;
  company_id: number;
  user_id?: number;
  user_name?: string;
  body: string;
  created_at: string;
}

export interface GoalActivityEntry {
  id: string;
  type: 'status_change' | 'comment' | 'watcher' | 'milestone';
  body: string;
  actor_name?: string;
  created_at: string;
}

export interface ProjectRock {
  id: number;
  name: string;
  status: string;
  priority: string;
  rock_status: string;
  owner_id?: number;
  owner_name?: string;
}

export interface GoalCycle {
  id: string;
  company_id: number;
  name: string;
  start_date: string;
  end_date: string;
  status: 'active' | 'closed';
  created_at: string;
  updated_at: string;
}

export interface GoalCycleSummaryRow {
  rock_status: string;
  priority: string;
  owner_name: string;
  count: number;
}

export interface GoalCycleSummary {
  cycle_id: number;
  total_rocks: number;
  completed_rocks: number;
  completion_pct: number;
  by_status: Record<string, number>;
  by_priority: Record<string, number>;
  by_owner: Record<string, number>;
  rows: GoalCycleSummaryRow[];
}

export interface GoalWatcher {
  id: string;
  goal_id: number;
  company_id: number;
  user_id: number;
  notify_pref: 'all' | 'mentions' | 'none';
  user_name?: string;
  created_at: string;
}

export interface GoalStatusLogEntry {
  id: string;
  goal_id: number;
  from_status?: string;
  to_status: string;
  changed_by?: number;
  changed_by_name?: string;
  changed_at: string;
}

export interface GoalTracker {
  id: string;
  company_id: number;
  goal_id: number;
  name: string;
  source_type: 'manual' | 'sage';
  current_value?: number;
  target_value?: number;
  last_synced_at?: string;
  created_at: string;
  updated_at: string;
}

export interface GoalTrackerSuggestion {
  name: string;
  source_type: 'manual' | 'sage';
  current_value?: number;
  target_value?: number;
  reasoning: string;
}

export interface GoalMilestone {
  id: string;
  name: string;
  status: string;
  priority: string;
  due_date?: string;
  assignee_id?: number;
  assignee_name?: string;
  notes?: string;
  goal_milestone_sort: number;
}

export type CreateAppTaskParams = {
  name: string;
  project_id?: number;
  /** Alternative to project_id — accepts a project's public_id UUID. Backend resolves
   *  to internal bigint. Preferred for frontend callers that only have the UUID. */
  project_public_id?: string;
  priority?: TaskPriority;
  status?: TaskStatus;
  type?: TaskType;
  assignee_id?: number;
  assignee_ids?: number[];
  contact_assignee_ids?: number[];
  goal_id?: number;
  notes?: string;
  due_date?: string;
  start_date?: string;
  end_date?: string;
  duration_minutes?: number;
  is_shop_task?: boolean;
  is_internal_billing?: boolean;
  repeat_type?: string;
  repeat_frequency?: string;
  repeat_value?: number;
  next_occurrence?: string;
  /** Spawn the next occurrence this many days before its due date. */
  spawn_days_before?: number;
  location_id?: number;
  entity_type?: string;
  entity_id?: number;
  inspection_outcome?: string | null;
  schedule_item_id?: number | null;
  labels?: string[];
  // Phase system (Sprint B2)
  schedule_type?: string;
  phase_id?: string | null;
  gantt_order?: number;
  subtask_sort_order?: number;
  // Subtask support
  parent_task_id?: string | null;
  // FS room assignment — UUID of the decision_folders row.
  room_folder_id?: string | null;
  // Estimate line item association (#2936).
  estimate_line_item_id?: number | null;
};

export type UpdateAppTaskParams = Partial<CreateAppTaskParams> & {
  parent_task_id?: string | null;
  clear_schedule_item?: boolean;
  clear_phase_id?: boolean;
  clear_estimate_line_item?: boolean;
  labels?: string[];
  /** Additive: append IDs to existing assignees without replacing */
  add_assignee_ids?: number[];
  /** Additive: append contact IDs to existing contact assignees without replacing */
  add_contact_assignee_ids?: number[];
  clear_room_folder_id?: boolean;
  /** Optimistic-concurrency precondition (#801) — the updated_at this write
   *  was based on. When set, a stale value returns 409 instead of silently
   *  clobbering a concurrent edit. Omit for plain last-write-wins. */
  expected_updated_at?: string;
};

export type AppTaskFilters = {
  project_id?: string;
  priority?: string;
  status?: string;
  type?: string;
  schedule_type?: string;
  phase_id?: string;
  assignee_id?: string;
  goal_id?: string;
  due_after?: string;
  due_before?: string;
  is_shop_task?: string;
  q?: string;
  sort?: string;
  dir?: string;
  limit?: number;
  cursor?: string;
  location_id?: string;
  entity_type?: string;
  entity_id?: string;
  schedule_item_id?: string;
};

export interface TaskComment {
  id: number;
  task_id: string;
  user_id: number | null;
  user_name: string;
  body: string;
  /** 'comment' = free-text posted by a user; 'event' = auto-logged field/status change. */
  kind: 'comment' | 'event';
  created_at: string;
  /** User IDs mentioned in this comment (task comments key mentions by ID, not name). */
  mentions?: number[];
  /** Attachment records uploaded via /tasks/{id}/attachments and attached to this comment. */
  attachments?: TaskCommentAttachment[];
}

export interface TaskCommentAttachment {
  id: number;
  url: string;
  thumbnail_url?: string;
  filename: string;
  original_name?: string;
  content_type?: string;
}

export const appTasks = {
  list: (params?: AppTaskFilters) => {
    const q = params ? new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)]))
    ).toString() : '';
    return request<PagedResponse<AppTask>>(`/api/v1/tasks${q ? '?' + q : ''}`);
  },
  get: (id: string) => request<any>(`/api/v1/tasks/${id}`).then(res => {
    // API wraps task in { task, completion_percent, subtask_count, subtasks, flag }
    if (res?.task) {
      const t = res.task as AppTask
      t.completion_percent = res.completion_percent ?? 0
      t.subtask_count = res.subtask_count ?? 0
      t.flag = res.flag ?? null
      return t
    }
    return res as AppTask
  }),
  create: (data: CreateAppTaskParams) =>
    request<AppTask>('/api/v1/tasks', { method: 'POST', ...body(data) }),
  update: (id: string, data: UpdateAppTaskParams) =>
    request<AppTask>(`/api/v1/tasks/${id}`, { method: 'PATCH', ...body(data) }),
  complete: (id: string) =>
    request<AppTask>(`/api/v1/tasks/${id}/complete`, { method: 'PATCH', ...body({}) }),
  delete: (id: string) =>
    request<void>(`/api/v1/tasks/${id}`, { method: 'DELETE' }),
  restore: (id: string) =>
    request<AppTask>(`/api/v1/tasks/${id}/restore`, { method: 'POST', ...body({}) }),
  duplicate: (id: string) =>
    request<AppTask>(`/api/v1/tasks/${id}/duplicate`, { method: 'POST' }),
  flag: (id: string, data: { reason: string; note?: string; photo_attachment_ids?: number[]; notify_user_ids?: number[] }) =>
    request<{ task: AppTask; flag: TaskFlag }>(`/api/v1/tasks/${id}/flag`, { method: 'POST', ...body(data) }),
  resolveFlag: (id: string, data?: { resolution_note?: string }) =>
    request<{ task: AppTask }>(`/api/v1/tasks/${id}/flag/resolve`, { method: 'POST', ...body(data ?? {}) }),
  listComments: (id: string) =>
    request<{ comments: TaskComment[]; total: number }>(`/api/v1/tasks/${id}/comments`),
  createComment: (id: string, taskBody: string, opts?: { mentions?: number[]; attachments?: TaskCommentAttachment[]; clientToken?: string }) =>
    request<TaskComment>(`/api/v1/tasks/${id}/comments`, { method: 'POST', ...body({
      body: taskBody, mentions: opts?.mentions, attachments: opts?.attachments, client_token: opts?.clientToken,
    }) }),
  listSubtasks: (id: string) =>
    request<{items: AppTask[], total: number, completion_percent: number}>(`/api/v1/tasks/${id}/subtasks`),
  createSubtask: (id: string, data: CreateAppTaskParams) =>
    request<AppTask>(`/api/v1/tasks/${id}/subtasks`, { method: 'POST', ...body(data) }),
  reorderSubtasks: (id: string, order: string[]) =>
    request<{reordered: boolean}>(`/api/v1/tasks/${id}/subtasks/reorder`, { method: 'PATCH', ...body({ order }) }),
  goals: (params?: { priority?: 'low' | 'medium' | 'high'; status?: string; owner_id?: string; cycle_id?: string; cycle_status?: string; parent_goal_id?: string; project_id?: string }) => {
    const qs = new URLSearchParams()
    if (params?.priority) qs.set('priority', params.priority)
    if (params?.status) qs.set('status', params.status)
    if (params?.owner_id) qs.set('owner_id', params.owner_id)
    if (params?.cycle_id) qs.set('cycle_id', params.cycle_id)
    if (params?.cycle_status) qs.set('cycle_status', params.cycle_status)
    if (params?.parent_goal_id) qs.set('parent_goal_id', params.parent_goal_id)
    if (params?.project_id) qs.set('project_id', params.project_id)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return request<AppGoal[]>(`/api/v1/goals${suffix}`)
  },
  createGoal: (data: { name: string; description?: string; status?: string; priority?: 'low' | 'medium' | 'high'; rock_status?: 'on_track' | 'at_risk' | 'off_track'; due_date?: string | null; owner_id?: number; cycle_id?: number; parent_goal_id?: number; project_id?: number }) =>
    request<AppGoal>('/api/v1/goals', { method: 'POST', ...body(data) }),
  updateGoal: (id: number | string, data: { name?: string; description?: string; status?: string; priority?: 'low' | 'medium' | 'high'; rock_status?: 'on_track' | 'at_risk' | 'off_track'; due_date?: string | null; clear_due_date?: boolean; owner_id?: number; clear_owner?: boolean; cycle_id?: number; clear_cycle?: boolean; parent_goal_id?: number; clear_parent?: boolean; project_id?: number; clear_project?: boolean }) =>
    request<AppGoal>(`/api/v1/goals/${id}`, { method: 'PATCH', ...body(data) }),
  deleteGoal: (id: number | string) =>
    request<void>(`/api/v1/goals/${id}`, { method: 'DELETE' }),
  listGoalMilestones: (goalId: number | string) =>
    request<GoalMilestone[]>(`/api/v1/goals/${goalId}/milestones`),
  createGoalMilestone: (goalId: number | string, data: { name: string; status?: string; priority?: string; due_date?: string | null; notes?: string }) =>
    request<GoalMilestone>(`/api/v1/goals/${goalId}/milestones`, { method: 'POST', ...body(data) }),
  linkGoalMilestone: (goalId: number | string, taskId: string) =>
    request<{ status: string }>(`/api/v1/goals/${goalId}/milestones/link`, { method: 'POST', ...body({ task_id: taskId }) }),
  patchGoalMilestone: (goalId: number | string, taskId: string, data: { sort_order?: number; status?: string; detach?: boolean }) =>
    request<{ status: string }>(`/api/v1/goals/${goalId}/milestones/${taskId}`, { method: 'PATCH', ...body(data) }),
  deleteGoalMilestone: (goalId: number | string, taskId: string) =>
    request<void>(`/api/v1/goals/${goalId}/milestones/${taskId}`, { method: 'DELETE' }),
  listGoalTrackers: (goalId: number | string) =>
    request<GoalTracker[]>(`/api/v1/goals/${goalId}/trackers`),
  createGoalTracker: (goalId: number | string, data: { name: string; current_value?: number; target_value?: number }) =>
    request<GoalTracker>(`/api/v1/goals/${goalId}/trackers`, { method: 'POST', ...body(data) }),
  updateGoalTracker: (goalId: number | string, trackerId: number | string, data: { name?: string; current_value?: number; target_value?: number }) =>
    request<GoalTracker>(`/api/v1/goals/${goalId}/trackers/${trackerId}`, { method: 'PATCH', ...body(data) }),
  deleteGoalTracker: (goalId: number | string, trackerId: number | string) =>
    request<void>(`/api/v1/goals/${goalId}/trackers/${trackerId}`, { method: 'DELETE' }),
  suggestGoalTrackers: (goalId: number | string, prompt?: string) =>
    request<{ suggestions: GoalTrackerSuggestion[] }>(`/api/v1/goals/${goalId}/suggest-trackers`, { method: 'POST', ...body({ prompt: prompt || '' }) }),
  listGoalStatusLog: (goalId: number | string) =>
    request<GoalStatusLogEntry[]>(`/api/v1/goals/${goalId}/status-log`),
  listGoalWatchers: (goalId: number | string) =>
    request<GoalWatcher[]>(`/api/v1/goals/${goalId}/watchers`),
  upsertGoalWatcher: (goalId: number | string, data: { user_id?: number; notify_pref?: string }) =>
    request<GoalWatcher>(`/api/v1/goals/${goalId}/watchers`, { method: 'POST', ...body(data) }),
  deleteGoalWatcher: (goalId: number | string, watcherId: number | string) =>
    request<void>(`/api/v1/goals/${goalId}/watchers/${watcherId}`, { method: 'DELETE' }),
  getMyGoalWatcher: (goalId: number | string) =>
    request<{ watching: boolean; watcher?: GoalWatcher }>(`/api/v1/goals/${goalId}/watchers/me`),
  // Goals P4
  listGoalComments: (goalId: number | string) =>
    request<GoalComment[]>(`/api/v1/goals/${goalId}/comments`),
  createGoalComment: (goalId: number | string, body_text: string) =>
    request<GoalComment>(`/api/v1/goals/${goalId}/comments`, { method: 'POST', ...body({ body: body_text }) }),
  deleteGoalComment: (goalId: number | string, commentId: string) =>
    request<void>(`/api/v1/goals/${goalId}/comments/${commentId}`, { method: 'DELETE' }),
  getGoalActivity: (goalId: number | string) =>
    request<GoalActivityEntry[]>(`/api/v1/goals/${goalId}/activity`),
  listGoalChildren: (goalId: number | string) =>
    request<AppGoal[]>(`/api/v1/goals/${goalId}/children`),
  getOffTrackRocksCount: () =>
    request<{ count: number }>('/api/v1/goals/my-off-track-count'),
  bulk: (task_ids: string[], action: string, payload?: Record<string, unknown>) =>
    request<{ affected: number; action: string }>('/api/v1/tasks/bulk', { method: 'POST', ...body({ task_ids, action, payload: payload || {} }) }),
};

export const goalCycles = {
  list: () => request<GoalCycle[]>('/api/v1/goal-cycles'),
  create: (data: { name: string; start_date: string; end_date: string; status?: string }) =>
    request<GoalCycle>('/api/v1/goal-cycles', { method: 'POST', ...body(data) }),
  update: (id: number | string, data: { name?: string; start_date?: string; end_date?: string; status?: string }) =>
    request<GoalCycle>(`/api/v1/goal-cycles/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number | string) =>
    request<void>(`/api/v1/goal-cycles/${id}`, { method: 'DELETE' }),
  myRockCount: (cycleId: number | string) =>
    request<{ count: number; warning: boolean; message: string }>(`/api/v1/goal-cycles/${cycleId}/my-rock-count`),
  close: (cycleId: number | string) =>
    request<GoalCycleSummary>(`/api/v1/goal-cycles/${cycleId}/close`, { method: 'POST' }),
  summary: (cycleId: number | string) =>
    request<GoalCycleSummary>(`/api/v1/goal-cycles/${cycleId}/summary`),
  carryForward: (cycleId: number | string, targetCycleId?: number | string) =>
    request<{ carried_forward: number; incomplete: Array<{ id: number; name: string; new_id?: number }> }>(
      `/api/v1/goal-cycles/${cycleId}/carry-forward`,
      { method: 'POST', ...body(targetCycleId ? { target_cycle_id: targetCycleId } : {}) }
    ),
};

// ── App Task Phases (Sprint B2) ──────────────────────────────────────────────

export const appPhases = {
  list: (params?: { project_id?: number | string; internal_only?: boolean }) => {
    const parts: string[] = [];
    if (params?.internal_only) parts.push('internal_only=true');
    else if (params?.project_id !== undefined && params.project_id !== '') parts.push(`project_id=${encodeURIComponent(String(params.project_id))}`);
    const q = parts.length ? `?${parts.join('&')}` : '';
    return request<AppTask[]>(`/api/v1/phases${q}`);
  },
  create: (data: { name: string; project_id?: number; project_public_id?: string; notes?: string; gantt_order?: number }) =>
    request<AppTask>('/api/v1/phases', { method: 'POST', ...body(data) }),
  update: (id: string, data: { name?: string; notes?: string; gantt_order?: number; start_date?: string; end_date?: string }) =>
    request<AppTask>(`/api/v1/phases/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: string) =>
    request<{ deleted: boolean }>(`/api/v1/phases/${id}`, { method: 'DELETE' }),
};

// ── Task Templates ────────────────────────────────────────────────────────────

export interface TaskTemplate {
  id: number;
  company_id: number;
  project_id?: number;
  name: string;
  description: string;
  trade_category: string;
  created_by?: number;
  created_at: string;
  updated_at: string;
  items?: TaskTemplateItem[];
}

export interface TaskTemplateItem {
  id: number;
  template_id: number;
  name: string;
  description: string;
  trade_category: string;
  estimated_hours?: number;
  sort_order: number;
  created_at: string;
}

export const taskTemplates = {
  list: (project_id?: string) =>
    request<{ data: TaskTemplate[]; total: number }>(`/api/v1/task-templates${project_id ? '?project_id=' + project_id : ''}`),
  get: (id: number) => request<TaskTemplate>(`/api/v1/task-templates/${id}`),
  create: (data: { name: string; description?: string; trade_category?: string; project_id?: number }) =>
    request<TaskTemplate>('/api/v1/task-templates', { method: 'POST', ...body(data) }),
  update: (id: number, data: Partial<{ name: string; description: string; trade_category: string; project_id: number }>) =>
    request<TaskTemplate>(`/api/v1/task-templates/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/task-templates/${id}`, { method: 'DELETE' }),
  listItems: (id: number) => request<{ data: TaskTemplateItem[]; total: number }>(`/api/v1/task-templates/${id}/items`),
  createItem: (id: number, data: { name: string; description?: string; trade_category?: string; estimated_hours?: number; sort_order?: number }) =>
    request<TaskTemplateItem>(`/api/v1/task-templates/${id}/items`, { method: 'POST', ...body(data) }),
  updateItem: (id: number, item_id: number, data: Partial<TaskTemplateItem>) =>
    request<TaskTemplateItem>(`/api/v1/task-templates/${id}/items/${item_id}`, { method: 'PATCH', ...body(data) }),
  deleteItem: (id: number, item_id: number) =>
    request<void>(`/api/v1/task-templates/${id}/items/${item_id}`, { method: 'DELETE' }),
  applyToProject: (project_id: string | number, template_id: number, start_date?: string) =>
    request<{ tasks_created: number; template_name: string }>(
      `/api/v1/projects/${project_id}/apply-template`,
      { method: 'POST', ...body({ template_id, start_date }) }
    ),
};

// ── Tasks (legacy project tasks) ──────────────────────────────────────────────

export const tasks = {
  list: (params?: { project_id?: number; status?: string; priority?: string; limit?: number; cursor?: string }) => {
    const q = params ? new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )).toString() : '';
    return request<PagedResponse<Task>>(`/api/v1/tasks${q ? '?' + q : ''}`);
  },
  get: (id: string) => request<Task>(`/api/v1/tasks/${id}`),
  create: (data: Partial<Task>) =>
    request<Task>('/api/v1/tasks', { method: 'POST', ...body(data) }),
  update: (id: string, data: Partial<Task>) =>
    request<Task>(`/api/v1/tasks/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: string) => request<void>(`/api/v1/tasks/${id}`, { method: 'DELETE' }),
};

// ── Comms ─────────────────────────────────────────────────────────────────────

export const comms = {
  markRead: (id: number) =>
    request<Comm>(`/api/v1/comms/${id}/read`, { method: 'POST' }),
  markAllRead: (projectId?: number) =>
    request<{marked_read: number}>(`/api/v1/comms/mark-all-read${projectId ? '?project_id=' + projectId : ''}`, { method: 'POST' }),
  list: (params?: { project_id?: number; channel?: string; direction?: string; limit?: number; cursor?: string }) => {
    const q = params ? new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )).toString() : '';
    return request<PagedResponse<Comm>>(`/api/v1/comms${q ? '?' + q : ''}`);
  },
  create: (data: Partial<Comm>) =>
    request<Comm>('/api/v1/comms', { method: 'POST', ...body(data) }),
  update: (id: number, data: { subject?: string; body?: string; tags?: string[] }) =>
    request<Comm>(`/api/v1/comms/${id}`, { method: 'PATCH', ...body(data) }),
  analyze: (id: number) =>
    request<Comm>(`/api/v1/comms/${id}/analyze`, { method: 'POST' }),
};

// ── Agent Events ──────────────────────────────────────────────────────────────

export const agentEvents = {
  list: (params?: { agent_name?: string; status?: string; limit?: number; cursor?: string }) => {
    const q = params ? new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )).toString() : '';
    return request<PagedResponse<AgentEvent>>(`/api/v1/agent-events${q ? '?' + q : ''}`);
  },
  create: (data: Partial<AgentEvent>) =>
    request<AgentEvent>('/api/v1/agent-events', { method: 'POST', ...body(data) }),
  update: (id: number, data: { status?: string; payload?: unknown }) =>
    request<AgentEvent>(`/api/v1/agent-events/${id}`, { method: 'PATCH', ...body(data) }),
};

// ── RFIs ──────────────────────────────────────────────────────────────────────

export const rfis = {
  list: (params?: { project_id?: number; status?: string; limit?: number; cursor?: string; priority?: string; assignee_id?: number }) => {
    const q = params ? new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )).toString() : '';
    return request<PagedResponse<RFI>>(`/api/v1/rfis${q ? '?' + q : ''}`);
  },
  get: (id: string) => request<RFI>(`/api/v1/rfis/${id}`),
  create: (data: Partial<RFI>) =>
    request<RFI>('/api/v1/rfis', { method: 'POST', ...body(data) }),
  update: (id: string, data: Partial<RFI>) =>
    request<RFI>(`/api/v1/rfis/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: string) => request<void>(`/api/v1/rfis/${id}`, { method: 'DELETE' }),
  duplicate: (id: string) => request<RFI>(`/api/v1/rfis/${id}/duplicate`, { method: 'POST' }),
  answer: (id: string, answer: string) =>
    request<{ ok: string }>(`/api/v1/rfis/${id}/answer`, { method: 'POST', ...body({ answer }) }),
  close: (id: string) =>
    request<{ ok: string }>(`/api/v1/rfis/${id}/close`, { method: 'POST' }),
  triggerCO: (id: string) =>
    request<{ co_id: number }>(`/api/v1/rfis/${id}/trigger-co`, { method: 'POST' }),
  listWatchers: (id: string) =>
    request<RFIWatcher[]>(`/api/v1/rfis/${id}/watchers`),
  addWatcher: (id: string, userId?: number) =>
    request<{ ok: string }>(`/api/v1/rfis/${id}/watchers`, { method: 'POST', ...body(userId ? { user_id: userId } : {}) }),
};

// ── Purchase Orders ───────────────────────────────────────────────────────────

export const purchaseOrders = {
  list: (params?: { project_id?: number; status?: string; supplier_id?: number; limit?: number; cursor?: string }) => {
    const q = params ? new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )).toString() : '';
    return request<PagedResponse<PurchaseOrder>>(`/api/v1/purchase-orders${q ? '?' + q : ''}`);
  },
  get: (id: string) => request<PurchaseOrder>(`/api/v1/purchase-orders/${id}`),
  create: (data: Partial<PurchaseOrder>) =>
    request<PurchaseOrder>('/api/v1/purchase-orders', { method: 'POST', ...body(data) }),
  update: (id: string, data: Partial<PurchaseOrder>) =>
    request<PurchaseOrder>(`/api/v1/purchase-orders/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: string) => request<void>(`/api/v1/purchase-orders/${id}`, { method: 'DELETE' }),
  send: (id: string) =>
    request<{ status: string }>(`/api/v1/purchase-orders/${id}/send`, { method: 'POST', ...body({}) }),
  receive: (id: string) =>
    request<{ status: string }>(`/api/v1/purchase-orders/${id}/receive`, { method: 'POST', ...body({}) }),
};


// ── Contacts (extended) ───────────────────────────────────────────────────────

export interface Contact {
  id: string;
  name: string;
  email: string;
  phone: string;
  type: 'client' | 'subcontractor' | 'sub' | 'vendor' | 'supplier' | 'employee' | 'architect' | 'external' | 'other';
  company: string;
  notes: string;
  address: string;
  trade: string;
  product_category?: string;
  role: string;
  created_at: string;
  default_cost_code_id?: number | null;
  parent_contact_id?: number | null;
  platform_access?: boolean;
  invite_sent_at?: string | null;
  user_id?: number | null;
  portal_status?: 'active' | 'invited' | 'not_invited' | string;
  last_login_at?: string | null;
  send_portal_invite?: boolean;
  project_id?: number | null;
  department?: string;
  employment_start_date?: string;
  archived_at?: string | null;
  is_company?: boolean;
  children?: Contact[];
}

export const contacts = {
  list: (params?: { type?: string; search?: string; limit?: number; cursor?: string }) => {
    const q = params ? new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )).toString() : '';
    return request<PagedResponse<Contact>>(`/api/v1/contacts${q ? '?' + q : ''}`);
  },
  get: (id: string) => request<Contact>(`/api/v1/contacts/${id}`),
  create: (data: Partial<Contact>) =>
    request<Contact>('/api/v1/contacts', { method: 'POST', ...body(data) }),
  update: (id: string, data: Partial<Contact>) =>
    request<Contact>(`/api/v1/contacts/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: string) => request<void>(`/api/v1/contacts/${id}`, { method: 'DELETE' }),
  merge: (sourceId: string, targetId: string) => request<Contact>(`/api/v1/contacts/${sourceId}/merge`, { method: 'POST', ...body({ target_id: targetId }) }),
  archive: (id: string) => request<{ archived: boolean; archived_at: string }>(`/api/v1/contacts/${id}/archive`, { method: 'POST' }),
  unarchive: (id: string) => request<{ archived: boolean; archived_at: null }>(`/api/v1/contacts/${id}/unarchive`, { method: 'POST' }),
  listArchived: (params?: { limit?: number; cursor?: string }) => {
    const q = params ? new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )).toString() : '';
    return request<PagedResponse<Contact>>(`/api/v1/contacts?archived_only=true${q ? '&' + q : ''}`);
  },
};

// ── Contact insurance policies + licenses (sub/supplier compliance) ─────────

export interface ContactInsurancePolicy {
  id: string;
  contact_id: number;
  policy_type: string;
  provider_name: string;
  policy_number: string;
  expiry_date: string;
  coverage_amount?: number | null;
  currency: string;
  notes: string;
  document_id?: number | null;
  file_path: string;
  file_name: string;
  alert_days: number;
  active: boolean;
  status: 'active' | 'expiring_soon' | 'expired';
  created_at: string;
  updated_at: string;
}

export interface ContactLicense {
  id: string;
  contact_id: number;
  license_type: string;
  license_number: string;
  issuing_authority: string;
  issued_date?: string | null;
  expiry_date?: string | null;
  notes: string;
  document_id?: number | null;
  file_path: string;
  file_name: string;
  alert_days: number;
  active: boolean;
  status: 'active' | 'expiring_soon' | 'expired';
  created_at: string;
  updated_at: string;
}

export interface ContactCompliance {
  contact_id: number;
  has_active_insurance: boolean;
  has_active_license: boolean;
  dismiss_insurance_warning: boolean;
  dismiss_license_warning: boolean;
}

export const contactInsurance = {
  list: (contactId: string) =>
    request<ContactInsurancePolicy[]>(`/api/v1/contacts/${contactId}/insurance-policies`),
  create: (contactId: string, data: Partial<ContactInsurancePolicy>) =>
    request<ContactInsurancePolicy>(`/api/v1/contacts/${contactId}/insurance-policies`, { method: 'POST', ...body(data) }),
  update: (contactId: string, policyId: string, data: Partial<ContactInsurancePolicy>) =>
    request<ContactInsurancePolicy>(`/api/v1/contacts/${contactId}/insurance-policies/${policyId}`, { method: 'PATCH', ...body(data) }),
  delete: (contactId: string, policyId: string) =>
    request<void>(`/api/v1/contacts/${contactId}/insurance-policies/${policyId}`, { method: 'DELETE' }),
};

export const contactLicenses = {
  list: (contactId: string) =>
    request<ContactLicense[]>(`/api/v1/contacts/${contactId}/licenses`),
  create: (contactId: string, data: Partial<ContactLicense>) =>
    request<ContactLicense>(`/api/v1/contacts/${contactId}/licenses`, { method: 'POST', ...body(data) }),
  update: (contactId: string, licenseId: string, data: Partial<ContactLicense>) =>
    request<ContactLicense>(`/api/v1/contacts/${contactId}/licenses/${licenseId}`, { method: 'PATCH', ...body(data) }),
  delete: (contactId: string, licenseId: string) =>
    request<void>(`/api/v1/contacts/${contactId}/licenses/${licenseId}`, { method: 'DELETE' }),
};

export const contactCompliance = {
  // Note: route is /compliance-status (not /compliance) — the latter is taken
  // by the older compliance-document CRUD handlers.
  get: (contactId: string) => request<ContactCompliance>(`/api/v1/contacts/${contactId}/compliance-status`),
  dismiss: (contactId: string, data: { dismiss_insurance_warning?: boolean; dismiss_license_warning?: boolean }) =>
    request<void>(`/api/v1/contacts/${contactId}/compliance-status/dismiss`, { method: 'PATCH', ...body(data) }),
};

// ── Project Phases ────────────────────────────────────────────────────────────

export interface ProjectPhase {
  id: string;
  project_id: number;
  name: string;
  color: string;
  sort_order: number;
  created_at: string;
}

export const projectPhases = {
  list: (params?: { project_id?: number; limit?: number }) => {
    const q = params ? new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )).toString() : '';
    return request<PagedResponse<ProjectPhase>>(`/api/v1/project-phases${q ? '?' + q : ''}`);
  },
  create: (data: Partial<ProjectPhase>) =>
    request<ProjectPhase>('/api/v1/project-phases', { method: 'POST', ...body(data) }),
  update: (id: number, data: Partial<ProjectPhase>) =>
    request<ProjectPhase>(`/api/v1/project-phases/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/project-phases/${id}`, { method: 'DELETE' }),
};

// ── Schedule Items ────────────────────────────────────────────────────────────

export interface ScheduleItem {
  id: string;
  project_id: number;
  phase_id?: number;
  title: string;
  assignee_id?: number;
  start_date: string;
  end_date: string;
  status: 'pending' | 'in_progress' | 'complete' | 'blocked';
  depends_on?: number;
  depends_on_title?: string;
  progress_pct: number;
  baseline_start?: string;
  baseline_end?: string;
  notes?: string;
  sub_confirmation_status?: string;
  created_at: string;
  updated_at: string;
  // Task aggregates
  task_count?: number;
  completed_task_count?: number;
  task_completion_pct?: number;
}

export const scheduleItems = {
  list: (params?: { project_id?: number; phase_id?: number; status?: string; limit?: number }) => {
    const q = params ? new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )).toString() : '';
    return request<PagedResponse<ScheduleItem>>(`/api/v1/schedule-items${q ? '?' + q : ''}`);
  },
  get: (id: number) => request<ScheduleItem>(`/api/v1/schedule-items/${id}`),
  create: (data: Partial<ScheduleItem>) =>
    request<ScheduleItem>('/api/v1/schedule-items', { method: 'POST', ...body(data) }),
  update: (id: number, data: Partial<ScheduleItem>) =>
    request<ScheduleItem>(`/api/v1/schedule-items/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/schedule-items/${id}`, { method: 'DELETE' }),
  /** Undo window for delete() — restores a soft-deleted schedule item (#2452). */
  restore: (id: number) => request<ScheduleItem>(`/api/v1/schedule-items/${id}/restore`, { method: 'POST' }),
  getTasksForItem: (id: number) => request<any[]>(`/api/v1/schedule-items/${id}/tasks`),
};

// ── Budget Items ──────────────────────────────────────────────────────────────

export interface BudgetItem {
  id: string;
  project_id: number;
  category: string;
  description: string;
  estimated: number;
  actual: number;
  forecasted: number;
  created_at: string;
  updated_at: string;
}

export const budgetItems = {
  list: (params?: { project_id?: number; limit?: number }) => {
    const q = params ? new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )).toString() : '';
    return request<PagedResponse<BudgetItem>>(`/api/v1/budget-items${q ? '?' + q : ''}`);
  },
  get: (id: number) => request<BudgetItem>(`/api/v1/budget-items/${id}`),
  create: (data: Partial<BudgetItem>) =>
    request<BudgetItem>('/api/v1/budget-items', { method: 'POST', ...body(data) }),
  update: (id: number, data: Partial<BudgetItem>) =>
    request<BudgetItem>(`/api/v1/budget-items/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/budget-items/${id}`, { method: 'DELETE' }),
};

// ── Change Orders ─────────────────────────────────────────────────────────────

export interface ChangeOrder {
  id: string;
  project_id: number;
  title: string;
  description: string;
  amount: number;
  status: 'draft' | 'pending_approval' | 'approved' | 'rejected';
  requested_by?: number;
  approved_at?: string;
  created_at: string;
  updated_at: string;
  /** Auto-classification when the CO was created from a workflow event.
   *  'decision_variance' = CO batches decision-option over-allowance items.
   *  'scope_change' | 'other' = other auto-sources. Absent for manual COs. */
  source?: string;
}

export const changeOrders = {
  list: (params?: { project_id?: number; status?: string; limit?: number }) => {
    const q = params ? new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )).toString() : '';
    return request<PagedResponse<ChangeOrder>>(`/api/v1/change-orders${q ? '?' + q : ''}`);
  },
  get: (id: string) => request<ChangeOrder>(`/api/v1/change-orders/${id}`),
  create: (data: Partial<ChangeOrder>) =>
    request<ChangeOrder>('/api/v1/change-orders', { method: 'POST', ...body(data) }),
  update: (id: string, data: Partial<ChangeOrder>) =>
    request<ChangeOrder>(`/api/v1/change-orders/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: string) => request<void>(`/api/v1/change-orders/${id}`, { method: 'DELETE' }),
};

// ── Estimates ─────────────────────────────────────────────────────────────────

export interface Estimate {
  id: string;
  company_id: number;
  project_id?: number;
  contact_id?: number;
  title: string;
  status: 'draft' | 'sent' | 'accepted' | 'declined';
  estimate_number: string;
  document_number: string;
  version: number;
  parent_estimate_id?: number;
  line_items: LineItem[];
  subtotal: number;
  tax_rate: number;
  total: number;
  notes: string;
  valid_until?: string;
  sent_at?: string;
  accepted_at?: string;
  created_at: string;
  updated_at: string;
  // Denormalized (from list queries)
  project_name?: string;
  contact_name?: string;
}

export interface LineItem {
  description: string;
  qty: number;
  unit: string;
  unit_price: number;
  total: number;
}

export interface EstimateEditorData {
  estimate: Estimate;
  line_items: EstimateLineItem[];
  project?: { id: number; public_id: string; name: string; address?: string; cover_image?: string };
  versions: EstimateVersion[];
  tax_lines: { id: number; name: string; rate: number }[];
  cost_codes: { id: number; code: string; name: string; category: string }[];
  overhead_templates: { id: number; name: string; rate_type: string; rate_value: number; apply_to_estimates: boolean; sort_order: number }[];
  insurance_templates: { id: number; name: string; cost_type: string; cost_value: number; apply_to_estimates: boolean; sort_order: number }[];
  project_contacts?: { id: string; name: string; email: string; phone?: string; company_name?: string }[];
  projects: { id: string; name: string }[];
  estimate_overheads: EstimateOverheadItem[];
  is_contract?: boolean;
}

export interface EstimateOverheadItem {
  id: number;
  estimate_id: number;
  company_id: number;
  name: string;
  rate_type: string;
  rate_value: number;
  amount: number;
  overhead_type: 'overhead' | 'insurance';
  source_id?: number;
  sort_order: number;
  created_at: string;
}

export const estimates = {
  list: (params?: { project_id?: number | string; contact_id?: number; status?: string; limit?: number }) => {
    const q = params ? new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )).toString() : '';
    return request<PagedResponse<Estimate>>(`/api/v1/estimates${q ? '?' + q : ''}`);
  },
  get: (id: string) => request<Estimate>(`/api/v1/estimates/${id}`),
  editorData: (id: string) => request<EstimateEditorData>(`/api/v1/estimates/${id}/editor-data`),
  create: (data: Partial<Estimate>) =>
    request<Estimate>('/api/v1/estimates', { method: 'POST', ...body(data) }),
  update: (id: string, data: Partial<Estimate>) =>
    request<Estimate>(`/api/v1/estimates/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: string) => request<void>(`/api/v1/estimates/${id}`, { method: 'DELETE' }),
  convertToBudget: (id: string, force?: boolean) =>
    request<{ proceed?: boolean; warnings?: string[]; missing_cost_code_items?: string[]; lines_created?: number; lines_updated?: number; total_lines?: number }>(
      `/api/v1/estimates/${id}/convert-to-budget${force ? '?force=true' : ''}`, { method: 'POST' }),
  applyMarkup: (id: string) =>
    request<{ updated: number }>(`/api/v1/estimates/${id}/apply-markup`, { method: 'POST' }),
  importCsv: (id: string, payload: { mode: 'replace' | 'merge' | 'new-version'; csv: string; version_name?: string }) =>
    request<{ ok: boolean; mode: string; inserted: number; updated: number; deleted: number; version_id?: number; version_name?: string; warnings?: string[] }>(
      `/api/v1/estimates/${id}/import-csv`,
      { method: 'POST', ...body(payload) },
    ),
};

export const estimateOverheads = {
  list: (estimateId: string) =>
    request<EstimateOverheadItem[]>(`/api/v1/estimates/${estimateId}/overheads`),
  seed: (estimateId: string) =>
    request<EstimateOverheadItem[]>(`/api/v1/estimates/${estimateId}/overheads/seed`, { method: 'POST' }),
  create: (estimateId: string, data: Partial<EstimateOverheadItem>) =>
    request<EstimateOverheadItem>(`/api/v1/estimates/${estimateId}/overheads`, { method: 'POST', ...body(data) }),
  update: (estimateId: string, itemId: number, data: Partial<EstimateOverheadItem>) =>
    request<EstimateOverheadItem>(`/api/v1/estimates/${estimateId}/overheads/${itemId}`, { method: 'PATCH', ...body(data) }),
  delete: (estimateId: string, itemId: number) =>
    request<void>(`/api/v1/estimates/${estimateId}/overheads/${itemId}`, { method: 'DELETE' }),
};

// ── Invoices ──────────────────────────────────────────────────────────────────

export interface Invoice {
  id: string;
  project_id: number;
  contact_id?: number;
  title: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'void';
  line_items: LineItem[];
  subtotal: number;
  tax_rate: number;
  total: number;
  due_date?: string;
  paid_at?: string;
  notes: string;
  created_at: string;
  updated_at: string;
  project_name?: string;
  document_number?: string;
}

export const invoices = {
  list: (params?: { project_id?: number; status?: string; limit?: number }) => {
    const q = params ? new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )).toString() : '';
    return request<PagedResponse<Invoice>>(`/api/v1/invoices${q ? '?' + q : ''}`);
  },
  get: (id: string) => request<Invoice>(`/api/v1/invoices/${id}`),
  create: (data: Partial<Invoice>) =>
    request<Invoice>('/api/v1/invoices', { method: 'POST', ...body(data) }),
  update: (id: string, data: Partial<Invoice>) =>
    request<Invoice>(`/api/v1/invoices/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: string) => request<void>(`/api/v1/invoices/${id}`, { method: 'DELETE' }),
  fromTimesheets: (data: { project_id?: number; project_public_id?: string; start_date: string; end_date: string; use_billing_rate?: boolean }) =>
    request<{ id: number; title: string; status: string; subtotal: number; total: number; line_items: any[]; created_at: string }>(
      '/api/v1/invoices/from-timesheets', { method: 'POST', ...body(data) }
    ),
};

// ── Subcontracts ──────────────────────────────────────────────────────────────

export interface Subcontract {
  id: string;
  project_id: number;
  contact_id?: number;
  title: string;
  scope: string;
  amount: number;
  status: 'draft' | 'sent' | 'signed' | 'complete';
  start_date?: string;
  end_date?: string;
  created_at: string;
  updated_at: string;
}

export const subcontracts = {
  list: (params?: { project_id?: number; status?: string; limit?: number }) => {
    const q = params ? new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )).toString() : '';
    return request<PagedResponse<Subcontract>>(`/api/v1/subcontracts${q ? '?' + q : ''}`);
  },
  get: (id: string) => request<Subcontract>(`/api/v1/subcontracts/${id}`),
  create: (data: Partial<Subcontract>) =>
    request<Subcontract>('/api/v1/subcontracts', { method: 'POST', ...body(data) }),
  update: (id: string, data: Partial<Subcontract>) =>
    request<Subcontract>(`/api/v1/subcontracts/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: string) => request<void>(`/api/v1/subcontracts/${id}`, { method: 'DELETE' }),
};

// ── Daily Reports ─────────────────────────────────────────────────────────────

export interface DailyReport {
  id: string;
  project_id: number;
  report_date: string;
  weather: string;
  crew_count: number;
  notes: string;
  created_by?: number;
  created_at: string;
  client_summary?: string;
  summary_generated_at?: string;
}

export const dailyReports = {
  list: (params?: { project_id?: number; limit?: number }) => {
    const q = params ? new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )).toString() : '';
    return request<PagedResponse<DailyReport>>(`/api/v1/daily-reports${q ? '?' + q : ''}`);
  },
  get: (id: number) => request<DailyReport>(`/api/v1/daily-reports/${id}`),
  create: (data: Partial<DailyReport>) =>
    request<DailyReport>('/api/v1/daily-reports', { method: 'POST', ...body(data) }),
  update: (id: number, data: Partial<DailyReport>) =>
    request<DailyReport>(`/api/v1/daily-reports/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/daily-reports/${id}`, { method: 'DELETE' }),
  summarize: (id: number) => request<DailyReport>(`/api/v1/daily-reports/${id}/summarize`, { method: 'POST' }),
};

// ── Documents ─────────────────────────────────────────────────────────────────

export interface Document {
  id: string;
  project_id?: number;
  entity_type: string;
  entity_id?: number;
  name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  uploaded_by?: number;
  created_at: string;
}

export const documents = {
  list: (params?: { project_id?: number; entity_type?: string; entity_id?: number; limit?: number }) => {
    const q = params ? new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )).toString() : '';
    return request<PagedResponse<Document>>(`/api/v1/documents${q ? '?' + q : ''}`);
  },
  create: (data: Partial<Document>) =>
    request<Document>('/api/v1/documents', { method: 'POST', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/documents/${id}`, { method: 'DELETE' }),
};

// ── Time Entries ──────────────────────────────────────────────────────────────

export interface TimeEntry {
  id: string;
  project_id: number;
  contact_id?: number;
  description: string;
  hours: number;
  entry_date: string;
  created_at: string;
}

export const timeEntries = {
  list: (params?: { project_id?: number; contact_id?: number; limit?: number }) => {
    const q = params ? new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )).toString() : '';
    return request<PagedResponse<TimeEntry>>(`/api/v1/time-entries${q ? '?' + q : ''}`);
  },
  get: (id: number) => request<TimeEntry>(`/api/v1/time-entries/${id}`),
  create: (data: Partial<TimeEntry>) =>
    request<TimeEntry>('/api/v1/time-entries', { method: 'POST', ...body(data) }),
  update: (id: number, data: Partial<TimeEntry>) =>
    request<TimeEntry>(`/api/v1/time-entries/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/time-entries/${id}`, { method: 'DELETE' }),
  restore: (id: number) => request<TimeEntry>(`/api/v1/time-entries/${id}/restore`, { method: 'POST' }),
};

// ── Leads ─────────────────────────────────────────────────────────────────────

export interface Lead {
  id: string;
  contact_id?: number;
  project_id?: number;
  title: string;
  status: string;
  value: number;
  source: string;
  notes: string;
  internal_notes: string;
  address: string;
  priority: string;
  lost_reason: string;
  assigned_to?: number;
  intake_submission_id?: number;
  expected_close?: string;
  last_activity_at: string;
  archived_at?: string;
  created_at: string;
  updated_at: string;
  // Hydrated
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  assigned_to_name?: string;
  project_name?: string;
}

export interface LeadActivity {
  id: number;
  lead_id: number;
  user_id?: number;
  activity_type: string;
  content: string;
  metadata: string;
  created_at: string;
  user_name?: string;
}

export const leads = {
  list: (params?: Record<string, string | number | boolean | undefined>) => {
    const q = params ? new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )).toString() : '';
    return request<PagedResponse<Lead>>(`/api/v1/leads${q ? '?' + q : ''}`);
  },
  get: (id: number) => request<Lead>(`/api/v1/leads/${id}`),
  create: (data: Partial<Lead>) =>
    request<Lead>('/api/v1/leads', { method: 'POST', ...body(data) }),
  update: (id: number, data: Partial<Lead>) =>
    request<Lead>(`/api/v1/leads/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/leads/${id}`, { method: 'DELETE' }),
  convert: (id: number) =>
    request<{ project_id: number; project_name: string; lead_status: string }>(
      `/api/v1/leads/${id}/convert`, { method: 'POST' }
    ),
  listActivities: (id: number) =>
    request<{ activities: LeadActivity[]; count: number }>(`/api/v1/leads/${id}/activities`),
  createActivity: (id: number, data: { activity_type: string; content: string; metadata?: string }) =>
    request<LeadActivity>(`/api/v1/leads/${id}/activities`, { method: 'POST', ...body(data) }),
};

// ── Selections ────────────────────────────────────────────────────────────────

export interface Selection {
  id: string;
  project_id: number;
  category: string;
  item: string;
  description: string;
  status: 'pending' | 'selected' | 'approved' | 'ordered';
  selected_by?: number;
  allowance?: number;
  actual_cost?: number;
  due_date?: string;
  created_at: string;
  updated_at: string;
}

export const selections = {
  list: (params?: { project_id?: number; category?: string; status?: string; limit?: number }) => {
    const q = params ? new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )).toString() : '';
    return request<PagedResponse<Selection>>(`/api/v1/selections${q ? '?' + q : ''}`);
  },
  get: (id: number) => request<Selection>(`/api/v1/selections/${id}`),
  create: (data: Partial<Selection>) =>
    request<Selection>('/api/v1/selections', { method: 'POST', ...body(data) }),
  update: (id: number, data: Partial<Selection>) =>
    request<Selection>(`/api/v1/selections/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/selections/${id}`, { method: 'DELETE' }),
};


// ── Generic helpers for new pages ─────────────────────────────────────────────
// These provide a simple fetch interface without typed response.

export const apiGet = (path: string) =>
  request<any>(`/api/v1${path}`);

export const apiPost = (path: string, data?: unknown) =>
  request<any>(`/api/v1${path}`, { method: 'POST', ...body(data) });

export const apiPatch = (path: string, data?: unknown) =>
  request<any>(`/api/v1${path}`, { method: 'PATCH', ...body(data) });

export const apiPut = (path: string, data?: unknown) =>
  request<any>(`/api/v1${path}`, { method: "PUT", ...body(data) });

export const apiDelete = (path: string) =>
  request<any>(`/api/v1${path}`, { method: 'DELETE' });

// Exposed so callers that construct fetch() manually (e.g. FormData uploads)
// resolve the correct host on native Capacitor (capacitor://localhost → production).
export const apiBaseUrl = BASE_URL;

// ── Estimate Line Items ───────────────────────────────────────────────────────

export interface EstimateLineItem {
  id: string;
  estimate_id: number;
  cost_code_id?: number;
  cost_code: string;
  catalog_item_id?: number;
  description: string;
  name_fr: string;
  quantity: number;
  unit: string;
  unit_cost: number;
  markup_pct: number;
  /** EST-17: 'percent' (default) uses markup_pct; 'flat' uses markup_flat_amount. */
  markup_type?: 'percent' | 'flat';
  markup_flat_amount?: number;
  tax_rate: number;
  subtotal: number;
  markup_amount: number;
  tax_amount: number;
  total: number;
  is_section_header: boolean;
  sort_order: number;
  company_id?: number;
  cost_type: string;
  line_status: string;
  builder_cost: number;
  tax_line_id?: number;
  parent_line_item_id?: number;
  takeoff_measurement_id?: number;
  quantity_formula?: string;
  equipment_id?: number;
  name_translations?: Record<string, string>;
  /** Rich-text markdown body (migration 00415). Paired with attachments via
   *  /api/v1/estimate-line-items/{id}/attachments. */
  description_md?: string;
  /** Read-only aggregate — number of rows in entity_attachments. */
  attachment_count?: number;
  /** UUID of the linked handbook procedure, if any. */
  procedure_id?: string;
  /** Free-text notes (migration 00143). No dedicated UI surface yet — API-only
   *  as of #2871; see EstimateItemSidePeek follow-up for adding an input. */
  notes?: string;
  /** Optimistic-concurrency token (#2722). The editor echoes this back as
   *  `expected_updated_at` on save; the server 409s if it no longer matches,
   *  so a concurrent edit by a co-worker is caught instead of silently
   *  overwritten. */
  updated_at?: string;
  /** UOW-4A (#3620) live co-drafting marker — present on create/update
   *  responses when the save changed draft-schedule state (task created/
   *  renamed/archived, phase upserted, materials fanned out). The editor
   *  refetches the batch links payload when `changed` is true, and shows a
   *  repair toast when `error` is set (the line itself still saved — the
   *  schedule sync is best-effort and never fails the save). */
  task_sync?: { changed: boolean; action?: string; task_id?: string; error?: string };
}

export interface EstimateVersion {
  id: number;
  estimate_id: number;
  version_number: number;
  version_name: string;
  is_working: boolean;
  created_at: string;
}

export const estimateLineItems = {
  list: (estimateId: string) => request<EstimateLineItem[]>(`/api/v1/estimates/${estimateId}/line-items`),
  create: (estimateId: string, data: Partial<EstimateLineItem>) =>
    request<EstimateLineItem>(`/api/v1/estimates/${estimateId}/line-items`, { method: 'POST', ...body(data) }),
  update: (estimateId: string, itemId: number, data: Partial<EstimateLineItem>) =>
    request<EstimateLineItem>(`/api/v1/estimates/${estimateId}/line-items/${itemId}`, { method: 'PATCH', ...body(data) }),
  /** 204 on a clean delete (resolves undefined). UOW-4A: when the guarded
   *  cascade could not run, the server answers 200 with a task_sync.error so
   *  the editor can prompt for repair — the line is deleted either way. */
  delete: (estimateId: string, itemId: number) =>
    request<{ deleted?: boolean; task_sync?: { changed?: boolean; error?: string } } | undefined>(
      `/api/v1/estimates/${estimateId}/line-items/${itemId}`, { method: 'DELETE' }),
  fromCatalog: (estimateId: string, catalogItemId: number, quantity: number) =>
    request<EstimateLineItem>(`/api/v1/estimates/${estimateId}/line-items/from-catalog`, {
      method: 'POST', ...body({ catalog_item_id: catalogItemId, quantity })
    }),
  syncToCatalog: (estimateId: string, itemId: number) =>
    request<{ catalog_item_id: number; unit_cost: number; message: string }>(`/api/v1/estimates/${estimateId}/line-items/${itemId}/sync-to-catalog`, { method: 'POST' }),
  saveToCatalog: (estimateId: string, itemId: number) =>
    request<{ catalog_item_id: number; message: string }>(`/api/v1/estimates/${estimateId}/line-items/${itemId}/save-to-catalog`, { method: 'POST' }),
  bulkSaveToCatalog: (estimateId: string, lineItemIds: number[]) =>
    request<{ created: number; updated: number }>(`/api/v1/estimates/${estimateId}/line-items/bulk-save-to-catalog`, { method: 'POST', ...body({ line_item_ids: lineItemIds }) }),
};

export interface LineItemStatus {
  id: number;
  company_id: number;
  slug: string;
  label: string;
  color: string;
  sort_order: number;
  is_default: boolean;
}

export const lineItemStatuses = {
  list: () => request<LineItemStatus[]>('/api/v1/settings/line-item-statuses'),
  create: (data: { label: string; slug?: string; color?: string; sort_order?: number }) =>
    request<LineItemStatus>('/api/v1/settings/line-item-statuses', { method: 'POST', ...body(data) }),
  update: (id: number, data: Partial<LineItemStatus>) =>
    request<LineItemStatus>(`/api/v1/settings/line-item-statuses/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) =>
    request<void>(`/api/v1/settings/line-item-statuses/${id}`, { method: 'DELETE' }),
};

export interface TakeoffMeasurement {
  id: number;
  sheet_id: number;
  sheet_name: string;
  layer_id: number;
  layer_name: string;
  label?: string;
  measurement_type: string;
  quantity?: number;
  unit?: string;
  real_world_value: number;
}

export interface StaleTakeoffLink {
  line_item_id: number;
  current_qty: number;
  takeoff_value: number;
  takeoff_label: string;
  measurement_id: number;
}

export interface DraftEstimateLineItem {
  description: string;
  quantity: number;
  unit: string;
  takeoff_measurement_id: number;
}

export interface DraftEstimateSection {
  name: string;
  line_items: DraftEstimateLineItem[];
}

export interface DraftEstimateResponse {
  sections: DraftEstimateSection[];
}

export const projectTakeoffMeasurements = {
  list: (projectId: string) =>
    request<TakeoffMeasurement[]>(`/api/v1/projects/${projectId}/takeoff-measurements`),
  staleLinks: (estimateId: string) =>
    request<StaleTakeoffLink[]>(`/api/v1/estimates/${estimateId}/stale-takeoff-links`),
  draftFromTakeoffs: (projectId: string) =>
    request<DraftEstimateResponse>(`/api/v1/projects/${projectId}/estimates/draft-from-takeoffs`, { method: 'POST' }),
};

export interface CostCatalogAssembly {
  id: string;
  name: string;
  category: string;
  description: string;
}

export const costCatalogAssemblies = {
  list: () => request<CostCatalogAssembly[]>('/api/v1/cost-catalog/assemblies'),
  create: (data: Partial<CostCatalogAssembly> & { items?: unknown[] }) =>
    request<CostCatalogAssembly>('/api/v1/cost-catalog/assemblies', { method: 'POST', ...body(data) }),
};

export interface CostCatalogItem {
  id: string;
  name: string;
  category: string;
  unit: string;
  unit_cost_ex_tax: number;
  markup: number;
  tax_rate: number;
  cost_code?: string;
  cost_code_id?: number;
  sku?: string;
  brand?: string;
  vendor?: string;
}

export const costCatalog = {
  list: (params?: { search?: string; category?: string; limit?: number }) => {
    const q = params ? new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )).toString() : '';
    return request<CostCatalogItem[]>(`/api/v1/cost-catalog${q ? '?' + q : ''}`);
  },
};


// ── Sprint 4 Types ─────────────────────────────────────────────────────────

export interface ProjectHealth {
  status: 'green' | 'yellow' | 'red';
  budget_score: number;
  schedule_score: number;
  issue_score: number;
  overall: number;
}

export interface DashboardMetrics {
  active_projects: number;
  health_breakdown: { green: number; yellow: number; red: number };
  this_month_revenue: number;
  outstanding: number;
  outstanding_count: number;
  overdue_items: number;
  overdue_tasks: number;
  overdue_invoices: number;
  todays_tasks: Array<{
    id: string;
    title: string;
    project_id: number | null;
    project_name: string;
    status: string;
    due_date: string;
  }>;
  needs_attention: Array<{
    type: 'invoice' | 'rfi' | 'task';
    id: string;
    title: string;
    project_id: number | null;
    project_name: string;
    days_overdue: number;
    amount?: number;
  }>;
  recent_projects: Array<{
    id: string;
    name: string;
    code: string;
    status: string;
    health: ProjectHealth;
    progress: number;
    next_milestone: string;
  }>;
}

export interface SearchResult {
  type: string;
  id: string;
  title: string;
  subtitle: string;
  url: string;
}

export interface SearchResponse {
  query: string;
  results: SearchResult[];
  total: number;
}

export interface InvoiceAgingSummary {
  current: { bucket: string; count: number; amount: number };
  days_1_30: { bucket: string; count: number; amount: number };
  days_31_60: { bucket: string; count: number; amount: number };
  days_61_90: { bucket: string; count: number; amount: number };
  over_90: { bucket: string; count: number; amount: number };
  total: number;
  total_count: number;
}

export interface PartialPayment {
  amount: number;
  date: string;
  method: string;
  notes: string;
  recorded_at: string;
  recorded_by: number;
}

export interface ChangeOrderSummary {
  approved: number;
  approved_count: number;
  pending: number;
  pending_count: number;
  rejected: number;
  rejected_count: number;
  net_impact: number;
}

// ── Sprint 4 API Functions ─────────────────────────────────────────────────

export const dashboard = {
  getMetrics: (): Promise<DashboardMetrics> => get('/dashboard'),
};

export const search = {
  query: (q: string): Promise<SearchResponse> => get(`/search?q=${encodeURIComponent(q)}`),
};

export const invoiceAgingApi = {
  getSummary: (): Promise<InvoiceAgingSummary> => get('/invoices/aging'),
  sendReminder: (invoiceId: string): Promise<{ success: boolean; message: string }> =>
    post(`/invoices/${invoiceId}/send-reminder`, {}),
};

export const changeOrderSummary = {
  getForProject: (projectId: string): Promise<ChangeOrderSummary> =>
    get(`/projects/${projectId}/change-orders/summary`),
};


// ── Sprint 5: Selection Categories ─────────────────────────────────────────

export interface SelectionCategory {
  id: string;
  project_id: number;
  name: string;
  description: string;
  allowance: number;
  sort_order: number;
  created_at: string;
  selected_option?: SelectionOption;
  choice_status: string;
}

export interface SelectionOption {
  id: string;
  category_id: number;
  name: string;
  description: string;
  vendor: string;
  sku: string;
  price: number;
  image_url: string;
  is_default: boolean;
  sort_order: number;
  created_at: string;
}

export interface SelectionChoice {
  id: string;
  category_id: number;
  option_id?: number;
  chosen_by?: number;
  chosen_at?: string;
  notes: string;
  status: 'pending' | 'selected' | 'approved' | 'ordered';
  created_at: string;
}

export interface SelectionSummary {
  total_allowance: number;
  total_selected: number;
  variance: number;
  pending_count: number;
  selected_count: number;
  approved_count: number;
  ordered_count: number;
}

export const selectionCategories = {
  list: (projectId: string) =>
    request<{ data: SelectionCategory[] }>(`/api/v1/projects/${projectId}/selections/categories`),
  create: (projectId: string, data: Partial<SelectionCategory>) =>
    request<SelectionCategory>(`/api/v1/projects/${projectId}/selections/categories`, { method: 'POST', ...body(data) }),
  get: (categoryId: number) =>
    request<SelectionCategory>(`/api/v1/selections/categories/${categoryId}`),
  update: (categoryId: number, data: Partial<SelectionCategory>) =>
    request<SelectionCategory>(`/api/v1/selections/categories/${categoryId}`, { method: 'PATCH', ...body(data) }),
  delete: (categoryId: number) =>
    request<void>(`/api/v1/selections/categories/${categoryId}`, { method: 'DELETE' }),
};

export const selectionOptions = {
  list: (categoryId: number) =>
    request<{ data: SelectionOption[] }>(`/api/v1/selections/categories/${categoryId}/options`),
  create: (categoryId: number, data: Partial<SelectionOption>) =>
    request<SelectionOption>(`/api/v1/selections/categories/${categoryId}/options`, { method: 'POST', ...body(data) }),
  update: (optionId: number, data: Partial<SelectionOption>) =>
    request<SelectionOption>(`/api/v1/selections/options/${optionId}`, { method: 'PATCH', ...body(data) }),
  delete: (optionId: number) =>
    request<void>(`/api/v1/selections/options/${optionId}`, { method: 'DELETE' }),
};

export const selectionChoices = {
  choose: (projectId: string, data: { category_id: number; option_id: number; notes?: string }) =>
    request<SelectionChoice>(`/api/v1/projects/${projectId}/selections/choose`, { method: 'POST', ...body(data) }),
  getSummary: (projectId: string) =>
    request<SelectionSummary>(`/api/v1/projects/${projectId}/selections/summary`),
};

// ── Sprint 5: Bid Requests ─────────────────────────────────────────────────

export interface BidRequest {
  id: string;
  project_id: number;
  title: string;
  description: string;
  scope_of_work: string;
  cost_code_id?: number;
  due_date?: string;
  status: 'draft' | 'sent' | 'invited' | 'received' | 'awarded' | 'declined' | 'closed';
  language: string;
  terms_and_conditions: string;
  sent_at?: string;
  created_at: string;
  updated_at: string;
  invite_count: number;
  response_count: number;
  low_bid: number;
}

export interface BidLineItem {
  id: number;
  // LI-1: richer shape now that bid request line items live in
  // document_line_items. Back-compat fields (description, quantity, unit)
  // still present; name + item_type + pricing + estimate provenance added.
  item_type: string;
  name: string;
  description: string;
  quantity: number;
  unit: string;
  unit_cost: number;
  markup_pct: number;
  tax_pct: number;
  cost_code_id?: number;
  estimate_line_item_id?: number;
  sort_order: number;
  notes?: string;
  // UOW-1C (#3588): origin task provenance — set when the line carries a
  // task_id directly or via its tracker source row. Drives the bid peek's
  // "Linked Task" open-origin affordance.
  task_id?: string;
  task_name?: string;
}

export interface BidInvite {
  id: string;
  bid_request_id: number;
  contact_id: number;
  token?: string;
  sent_at?: string;
  viewed_at?: string;
  status: string;
  contact_name: string;
  contact_email: string;
}

export interface BidResponse {
  id: string;
  bid_request_id: number;
  contact_id: number;
  amount: number;
  notes: string;
  attachment_url: string;
  submitted_at: string;
  is_winner: boolean;
  contact_name: string;
  contact_email: string;
}

export const bidRequests = {
  listAll: (params?: { project_id?: number; status?: string; limit?: number }) => {
    const q = params ? new URLSearchParams(Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    )).toString() : '';
    return request<{ data: BidRequest[]; has_more: boolean; next_cursor: number }>(`/api/v1/bid-requests${q ? '?' + q : ''}`);
  },
  list: (projectId: string) =>
    request<{ data: BidRequest[] }>(`/api/v1/projects/${projectId}/bid-requests`),
  createGlobal: (data: Partial<BidRequest> & { project_id: number }) =>
    request<BidRequest>('/api/v1/bid-requests', { method: 'POST', ...body(data) }),
  create: (projectId: string, data: Partial<BidRequest>) =>
    request<BidRequest>(`/api/v1/projects/${projectId}/bid-requests`, { method: 'POST', ...body(data) }),
  get: (bidRequestId: number) =>
    request<BidRequest>(`/api/v1/bid-requests/${bidRequestId}`),
  update: (bidRequestId: number, data: Partial<BidRequest>) =>
    request<BidRequest>(`/api/v1/bid-requests/${bidRequestId}`, { method: 'PATCH', ...body(data) }),
  delete: (bidRequestId: number) =>
    request<void>(`/api/v1/bid-requests/${bidRequestId}`, { method: 'DELETE' }),
  listInvites: (bidRequestId: number) =>
    request<{ data: BidInvite[] }>(`/api/v1/bid-requests/${bidRequestId}/invites`),
  addInvite: (bidRequestId: number, contactId: number) =>
    request<{ status: string }>(`/api/v1/bid-requests/${bidRequestId}/invite`, { method: 'POST', ...body({ contact_id: contactId }) }),
  sendInvites: (bidRequestId: number) =>
    request<{ status: string }>(`/api/v1/bid-requests/${bidRequestId}/send`, { method: 'POST', ...body({}) }),
  listResponses: (bidRequestId: number) =>
    request<{ data: BidResponse[] }>(`/api/v1/bid-requests/${bidRequestId}/responses`),
  createResponse: (bidRequestId: number, data: Partial<BidResponse>) =>
    request<BidResponse>(`/api/v1/bid-requests/${bidRequestId}/responses`, { method: 'POST', ...body(data) }),
  awardBid: (bidRequestId: number, contactId: number) =>
    request<{ status: string }>(`/api/v1/bid-requests/${bidRequestId}/award/${contactId}`, { method: 'POST', ...body({}) }),
  listLineItems: (bidRequestId: number) =>
    request<BidLineItem[]>(`/api/v1/bid-requests/${bidRequestId}/line-items`),
  createLineItem: (bidRequestId: number, data: Partial<BidLineItem>) =>
    request<{ id: number }>(`/api/v1/bid-requests/${bidRequestId}/line-items`, { method: 'POST', ...body(data) }),
  // LI-1: new update endpoint
  updateLineItem: (bidRequestId: number, itemId: number, data: Partial<BidLineItem>) =>
    request<{ ok: string }>(`/api/v1/bid-requests/${bidRequestId}/line-items/${itemId}`, { method: 'PATCH', ...body(data) }),
  deleteLineItem: (bidRequestId: number, itemId: number) =>
    request<{ ok: string }>(`/api/v1/bid-requests/${bidRequestId}/line-items/${itemId}`, { method: 'DELETE' }),
};

// ── Sprint 5: Submittals ───────────────────────────────────────────────────

export interface Submittal {
  id: string;
  project_id: number;
  submittal_number: string;
  title: string;
  description: string;
  spec_section: string;
  submitted_by?: number;
  submitted_at?: string;
  reviewer_id?: number;
  reviewed_at?: string;
  due_date?: string;
  status: 'draft' | 'pending' | 'submitted' | 'under_review' | 'approved' | 'approved_as_noted' | 'approved_with_comments' | 'revise_resubmit' | 'resubmit_required' | 'rejected' | 'closed';
  review_notes: string;
  ball_in_court: string;
  created_at: string;
  days_open: number;
  submitted_by_name?: string;
  reviewer_name?: string;
  submittal_type: string;
  assignee_id?: number;
  // Mutually exclusive with assignee_id — a contact with no platform login (#1282).
  contact_assignee_id?: number;
  assignee_name?: string;
  received_date?: string;
  returned_date?: string;
  created_by?: number;
  revision: number;
}

export interface SubmittalReviewer {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  /** Set instead of user_id/user_name when this reviewer is an external, non-user CONTACT (#2739). */
  contact_id?: number;
  contact_name?: string;
  contact_email?: string;
  contact_company?: string;
  is_external: boolean;
  review_order: number;
  status: 'pending' | 'approved' | 'approved_with_comments' | 'rejected';
  reviewed_at?: string;
  comments: string;
}

export interface SubmittalAttachment {
  id: string;
  submittal_id: number;
  file_name: string;
  file_url: string;
  file_size: number;
  uploaded_at: string;
}

export const submittals = {
  list: (projectId: string) =>
    request<{ data: Submittal[] }>(`/api/v1/projects/${projectId}/submittals`),
  create: (projectId: string, data: Partial<Submittal>) =>
    request<Submittal>(`/api/v1/projects/${projectId}/submittals`, { method: 'POST', ...body(data) }),
  get: (submittalId: number) =>
    request<Submittal>(`/api/v1/submittals/${submittalId}`),
  update: (submittalId: number, data: Partial<Submittal>) =>
    request<Submittal>(`/api/v1/submittals/${submittalId}`, { method: 'PATCH', ...body(data) }),
  delete: (submittalId: number) =>
    request<void>(`/api/v1/submittals/${submittalId}`, { method: 'DELETE' }),
  submit: (submittalId: number) =>
    request<Submittal>(`/api/v1/submittals/${submittalId}/submit`, { method: 'POST', ...body({}) }),
  review: (submittalId: number, data: { status: string; review_notes: string }) =>
    request<Submittal>(`/api/v1/submittals/${submittalId}/review`, { method: 'POST', ...body(data) }),
  listAttachments: (submittalId: number) =>
    request<{ data: SubmittalAttachment[] }>(`/api/v1/submittals/${submittalId}/attachments`),
  addAttachment: (submittalId: number, data: { file_name: string; file_url: string; file_size: number }) =>
    request<SubmittalAttachment>(`/api/v1/submittals/${submittalId}/attachments`, { method: 'POST', ...body(data) }),
  revise: (submittalId: number, notes?: string) =>
    request<{ revision: number; status: string }>(`/api/v1/submittals/${submittalId}/revise`, { method: 'POST', ...body({ notes: notes || '' }) }),
  listReviewers: (submittalId: number) =>
    request<SubmittalReviewer[]>(`/api/v1/submittals/${submittalId}/reviewers`),
  addReviewer: (submittalId: number, data: { user_id?: number; contact_id?: number; review_order?: number }) =>
    request<{ ok: string }>(`/api/v1/submittals/${submittalId}/reviewers`, { method: 'POST', ...body(data) }),
};

// ── Sprint 5: Timesheets ───────────────────────────────────────────────────

export interface TimesheetEntry {
  id: string;
  user_id?: number;
  project_id?: number;
  date: string;
  clock_in?: string;
  clock_out?: string;
  break_minutes: number;
  hours: number;
  description: string;
  cost_code_id?: number;
  billable: boolean;
  approved_by?: number;
  approved_at?: string;
  status: 'pending' | 'submitted' | 'approved' | 'rejected' | 'paid';
  paid_at?: string;
  created_at: string;
  // TS-P1-OT-ENGINE: OT breakdown. regular_hours is null until an OT calc has
  // run (on manager approval); overtime_hours/double_time_hours are the premium
  // blocks; ot_type classifies them as 'approved' (paid at the OT multiplier)
  // vs 'voluntary' (banked as time in lieu when banking is enabled).
  regular_hours?: number;
  overtime_hours?: number;
  double_time_hours?: number;
  ot_type?: 'approved' | 'voluntary' | null;
  /** True when this entry's hours have been billed to the client on an invoice.
   *  Orthogonal to paid (payroll). Not yet surfaced by GET /timesheets — see #1350
   *  follow-up; the "· billed" payroll marker stays hidden until the API sets it. */
  invoiced?: boolean;
  user_name?: string;
  project_name?: string;
  project_public_id?: string;
  // M2 fields
  split_entries?: SplitEntry[];
  work_item_id?: number;
  labels?: string[];
  hourly_cost?: number;
  /** True when submitted with no project; user confirmed internal billing. */
  is_internal_billing?: boolean;
  /** TS-P4-GPS-AUTOCLOCKIN: punch fell outside the project's geofence — advisory only. */
  location_flagged?: boolean;
  /** TS-P2-SPLIT-ENTRY: set when this entry was replaced by a split. */
  voided_at?: string;
  parent_entry_id?: number;
  /** OFFLINE-CLOCK-01 (#2457): true when the clock-in/out tap was captured while
   *  offline and queued for later sync — surfaced for manager review. */
  clock_in_offline_capture?: boolean;
  clock_out_offline_capture?: boolean;
  /** TS-PUNCH-TIME-EDIT: true when the punch time was hand-set (the crew member
   *  forgot to punch) rather than tapped — advisory, shown to approvers. */
  clock_in_edited?: boolean;
  clock_out_edited?: boolean;
}

export interface SplitEntry {
  cost_code_id: number;
  minutes: number;
}

/** TS-P2-SPLIT-ENTRY: one row of a first-class entry split (distinct from
 * SplitEntry above, which is the pre-existing minutes-based cost-code
 * breakdown stored inline on a single entry). */
export interface TimesheetSplitPart {
  project_id?: number;
  project_public_id?: string;
  cost_code_id?: number;
  hours: number;
}

export const timesheets = {
  list: (params?: { user_id?: number; project_id?: number; start_date?: string; end_date?: string; status?: string }) => {
    const q = new URLSearchParams();
    if (params?.user_id) q.set('user_id', String(params.user_id));
    if (params?.project_id) q.set('project_id', String(params.project_id));
    if (params?.start_date) q.set('start_date', params.start_date);
    if (params?.end_date) q.set('end_date', params.end_date);
    if (params?.status) q.set('status', params.status);
    return request<{ data: TimesheetEntry[] }>(`/api/v1/timesheets${q.toString() ? '?' + q : ''}`);
  },
  create: (data: Partial<TimesheetEntry>) =>
    request<TimesheetEntry>('/api/v1/timesheets', { method: 'POST', ...body(data) }),
  get: (entryId: number) =>
    request<TimesheetEntry>(`/api/v1/timesheets/${entryId}`),
  update: (entryId: number, data: Partial<TimesheetEntry>) =>
    request<TimesheetEntry>(`/api/v1/timesheets/${entryId}`, { method: 'PATCH', ...body(data) }),
  delete: (entryId: number) =>
    request<void>(`/api/v1/timesheets/${entryId}`, { method: 'DELETE' }),
  restore: (entryId: number) =>
    request<TimesheetEntry>(`/api/v1/timesheets/${entryId}/restore`, { method: 'POST' }),
  split: (entryId: number, splits: TimesheetSplitPart[]) =>
    request<{ voided_entry_id: number; children: TimesheetEntry[] }>(`/api/v1/timesheets/${entryId}/split`, { method: 'POST', ...body({ splits }) }),
  // started_at / ended_at (TS-PUNCH-TIME-EDIT) are a DELIBERATE punch time set
  // by the user who forgot to punch — validated loudly server-side and flagged
  // on the entry. Do not confuse with captured_at, which is the offline tap
  // time and falls back silently. See docs/features/timesheets-punch-time-edit.md.
  clockIn: (data: { user_id?: number; project_id?: number; project_public_id?: string; cost_code_id?: number; notes?: string; captured_at?: string; client_token?: string; offline_capture?: boolean; lat?: number; lng?: number; started_at?: string }) =>
    request<TimesheetEntry>('/api/v1/timesheets/clock-in', { method: 'POST', ...body(data) }),
  clockOut: (data: { entry_id?: number; break_minutes?: number; notes?: string; confirm_no_project?: boolean; captured_at?: string; client_token?: string; offline_capture?: boolean; lat?: number; lng?: number; ended_at?: string }) =>
    request<{ entry_id: number; hours: number }>('/api/v1/timesheets/clock-out', { method: 'POST', ...body(data) }),
  getActive: () =>
    request<{
      active: boolean
      id?: number
      project_id?: number | null
      project_public_id?: string | null
      project_name?: string | null
      cost_code_id?: number | null
      started_at?: string
      paused?: boolean
      paused_at?: string | null
      elapsed_seconds?: number
      notes?: string
      started_at_edited?: boolean
    }>('/api/v1/timesheets/active'),
  updateActive: (data: { project_public_id?: string; cost_code_id?: number | null; notes?: string; started_at?: string }) =>
    request<{
      active: boolean
      id?: number
      project_id?: number | null
      project_public_id?: string | null
      project_name?: string | null
      cost_code_id?: number | null
      started_at?: string
      paused?: boolean
      paused_at?: string | null
      elapsed_seconds?: number
      notes?: string
      started_at_edited?: boolean
    }>('/api/v1/timesheets/active', { method: 'PATCH', ...body(data) }),
  pause: () =>
    request<{ paused: boolean }>('/api/v1/timesheets/pause', { method: 'POST', ...body({}) }),
  resume: () =>
    request<{ paused: boolean }>('/api/v1/timesheets/resume', { method: 'POST', ...body({}) }),
  switchTimer: (data: { project_id?: number; project_public_id?: string; cost_code_id?: number; notes?: string }) =>
    request<{ id: number; started_at: string; switched: boolean }>('/api/v1/timesheets/switch', { method: 'POST', ...body(data) }),
  manualEntry: (data: {
    project_id?: number;
    project_public_id?: string;
    cost_code_id?: number;
    clock_in: string;
    clock_out: string;
    break_minutes?: number;
    notes?: string;
  }) =>
    request<{ entry_id: number; hours: number }>('/api/v1/timesheets/manual', { method: 'POST', ...body(data) }),
  getWeek: (date?: string, projectId?: string | number) => {
    const q = new URLSearchParams();
    if (date) q.set('date', date);
    if (projectId) q.set('project_id', String(projectId));
    return request<{ data: TimesheetEntry[]; week_start: string; week_end: string }>(`/api/v1/timesheets/week${q.toString() ? '?' + q : ''}`);
  },
  approve: (entryIds: number[]) =>
    request<{ status: string }>('/api/v1/timesheets/approve', { method: 'POST', ...body({ entry_ids: entryIds }) }),
  submit: (entryId: number, opts?: { confirmNoProject?: boolean }) =>
    request<{ status: string }>(`/api/v1/timesheets/${entryId}/submit`, {
      method: 'PATCH',
      ...(opts?.confirmNoProject ? body({ confirm_no_project: true }) : {}),
    }),
  multiUser: (data: {
    user_ids: number[];
    project_id?: number;
    project_public_id?: string;
    date?: string;
    hours?: number;
    break_minutes?: number;
    description?: string;
    cost_code_id?: number;
    billable?: boolean;
    labels?: string[];
    split_entries?: SplitEntry[];
    // TS-P3-CLOCK-OTHERS (#1115): opts into starting an open active_timers
    // row per user_id (each independently clock-out-able) instead of the
    // default closed-entry historical batch create. Requires
    // timesheets.clock_in_others (or edit_others).
    live_clock_in?: boolean;
  }) =>
    request<{
      created: number;
      ids?: number[];
      started?: { user_id: number; timer_id: number; started_at: string }[];
      skipped_already_clocked_in?: number[];
    }>('/api/v1/timesheets/multi-user', { method: 'POST', ...body(data) }),
  approveEntry: (entryId: number) =>
    request<{ status: string }>(`/api/v1/timesheets/${entryId}/approve`, { method: 'PATCH' }),
  bulkApprove: (ids: number[]) =>
    request<{ approved: number }>('/api/v1/timesheets/bulk-approve', { method: 'PATCH', ...body({ ids }) }),
  bulkPaid: (ids: number[]) =>
    request<{ paid: number; skipped: number }>('/api/v1/timesheets/bulk-paid', { method: 'PATCH', ...body({ ids }) }),
  bulkReject: (ids: number[], reason: string) =>
    request<{ rejected: number }>('/api/v1/timesheets/bulk-reject', { method: 'PATCH', ...body({ ids, reason }) }),
  rejectEntry: (entryId: number, reason: string) =>
    request<{ status: string }>(`/api/v1/timesheets/${entryId}/reject`, { method: 'POST', ...body({ reason }) }),
  timesheetSummary: (projectId: string) =>
    request<{ total_hours: number; approved_hours: number; pending_hours: number; total_cost: number; hours_by_phase: { phase: string; hours: number }[] }>(`/api/v1/projects/${projectId}/timesheet-summary`),
};

// ── Sprint 5: Project Team ─────────────────────────────────────────────────

export interface ProjectTeamMember {
  id: string;
  project_id: number;
  user_id: number;
  role: string;
  assigned_at: string;
  user_name: string;
  user_email: string;
  // True for admins / any all_projects_access user (#2087) — they see every
  // project via a company-wide flag rather than an explicit project_team row.
  is_implicit?: boolean;
}

export const projectTeam = {
  list: (projectId: string) =>
    request<{ data: ProjectTeamMember[] }>(`/api/v1/projects/${projectId}/team`),
  add: (projectId: string, data: { user_id: number; role: string }) =>
    request<ProjectTeamMember>(`/api/v1/projects/${projectId}/team`, { method: 'POST', ...body(data) }),
  remove: (projectId: string, userId: number) =>
    request<void>(`/api/v1/projects/${projectId}/team/${userId}`, { method: 'DELETE' }),
};

// ── Sprint 5: Messages ─────────────────────────────────────────────────────

export interface ProjectMessage {
  id: string;
  project_id: number;
  sender_id?: number;
  sender_contact_id?: number;
  message: string;
  is_internal: boolean;
  created_at: string;
  sender_name: string;
  sender_avatar?: string;
}

export const projectMessages = {
  list: (projectId: string, includeInternal = false) =>
    request<{ data: ProjectMessage[] }>(`/api/v1/projects/${projectId}/messages${includeInternal ? '?internal=true' : ''}`),
  create: (projectId: string, data: { message: string; is_internal: boolean }) =>
    request<ProjectMessage>(`/api/v1/projects/${projectId}/messages`, { method: 'POST', ...body(data) }),
};

// ── Sprint 5: Team Management ──────────────────────────────────────────────

export interface TeamUser {
  id: string;
  email: string;
  name: string;
  role: string;
  phone: string;
  title: string;
  hourly_rate?: number;
  billing_rate?: number;
  cost_rate?: number;
  can_approve_timesheets: boolean;
  can_approve_invoices: boolean;
  default_cost_code_id?: number | null;
}

// ── Cost Codes ────────────────────────────────────────────────────────────────

export interface CostCodeItem {
  id: number;
  code: string;
  name: string;
  category: string;
  description: string;
  is_overhead_default: boolean;
}

export const costCodes = {
  list: () => request<{ all: CostCodeItem[]; grouped: { category: string; items: CostCodeItem[] }[] }>('/api/v1/cost-codes'),
};

export const teamManagement = {
  list: () =>
    request<{ data: TeamUser[] }>('/api/v1/team'),
  update: (id: number, data: Partial<TeamUser>) =>
    request<{ status: string }>(`/api/v1/team/${id}`, { method: 'PATCH', ...body(data) }),
};

// ── Sprint 11: Photos, Drawings, Punch Lists ──────────────────────────────────

export interface ProjectPhoto {
  id: string;
  project_id: number;
  file_name: string;
  file_url: string;
  thumbnail_url: string;
  file_size: number;
  mime_type: string;
  width?: number;
  height?: number;
  caption: string;
  location: string;
  tags: string[];
  taken_at?: string;
  taken_by?: number;
  daily_report_id?: number;
  task_id?: number;
  rfi_log_id?: number;
  is_before: boolean;
  is_after: boolean;
  shared_with_client: boolean;
  sort_order: number;
  created_at: string;
}

export interface Drawing {
  id: string;
  project_id: number;
  sheet_number: string;
  title: string;
  discipline: string;
  revision: string;
  file_name: string;
  file_url: string;
  thumbnail_url: string;
  file_size: number;
  upload_date: string;
  is_current: boolean;
  sort_order: number;
  created_at: string;
}

export interface DrawingAnnotation {
  id: string;
  drawing_id: number;
  user_id?: number;
  user_name?: string;
  x_pct: number;
  y_pct: number;
  annotation_type: 'note' | 'rfi' | 'punch' | 'dimension';
  content: string;
  linked_rfi_log_id?: number;
  linked_punch_id?: number;
  color: string;
  is_resolved: boolean;
  created_at: string;
}

export interface DrawingWithAnnotations {
  drawing: Drawing;
  annotations: DrawingAnnotation[];
}

export interface PunchList {
  id: string;
  project_id: number;
  name: string;
  walkthrough_date?: string;
  created_by?: number;
  created_by_name?: string;
  status: 'draft' | 'in_progress' | 'submitted' | 'complete';
  notes: string;
  created_at: string;
  item_count?: number;
  completed_count?: number;
}

export interface PunchItem {
  id: string;
  punch_list_id: number;
  item_number: number;
  location: string;
  description: string;
  assigned_to?: number;
  assignee_name?: string;
  priority: 'low' | 'normal' | 'high' | 'critical';
  status: 'open' | 'in_progress' | 'ready_for_review' | 'complete' | 'not_applicable';
  due_date?: string;
  completed_at?: string;
  completed_by?: number;
  completed_by_name?: string;
  notes: string;
  created_at: string;
  photos?: ProjectPhoto[];
}

export interface PunchListSummary {
  total: number;
  open: number;
  in_progress: number;
  ready_for_review: number;
  complete: number;
  not_applicable: number;
}

export const photos = {
  list: (projectId: string, params?: { location?: string }) =>
    get<ProjectPhoto[]>(`/api/v1/projects/${projectId}/photos` + (params?.location ? `?location=${params.location}` : '')),
  get: (id: number) => get<ProjectPhoto>(`/api/v1/photos/${id}`),
  upload: (projectId: string, formData: FormData) =>
    fetch(`${BASE_URL}/api/v1/projects/${projectId}/photos`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getToken()}` },
      body: formData
    }).then(r => r.json()),
  update: (id: number, data: Partial<ProjectPhoto>) => patch<ProjectPhoto>(`/api/v1/photos/${id}`, data),
  delete: (id: number) => del(`/api/v1/photos/${id}`),
  restore: (id: number) => post<ProjectPhoto>(`/api/v1/photos/${id}/restore`),
  locations: (projectId: string) => get<string[]>(`/api/v1/projects/${projectId}/photos/locations`),
  setBefore: (id: number) => post(`/api/v1/photos/${id}/set-before`),
  setAfter: (id: number) => post(`/api/v1/photos/${id}/set-after`),
  comparisons: (projectId: string) => get<{ location: string; before?: ProjectPhoto; after?: ProjectPhoto }[]>(`/api/v1/projects/${projectId}/photos/comparisons`),
};

export const drawings = {
  list: (projectId: string, params?: { discipline?: string; current?: boolean }) => {
    const q = new URLSearchParams();
    if (params?.discipline) q.set('discipline', params.discipline);
    if (params?.current) q.set('current', 'true');
    return get<Drawing[]>(`/api/v1/projects/${projectId}/drawings` + (q.toString() ? `?${q}` : ''));
  },
  get: (id: number) => get<DrawingWithAnnotations>(`/api/v1/drawings/${id}`),
  upload: (projectId: string, formData: FormData) =>
    fetch(`${BASE_URL}/api/v1/projects/${projectId}/drawings`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getToken()}` },
      body: formData
    }).then(r => r.json()),
  delete: (id: number) => del(`/api/v1/drawings/${id}`),
  disciplines: (projectId: string) => get<string[]>(`/api/v1/projects/${projectId}/drawings/disciplines`),
  addAnnotation: (drawingId: number, data: Partial<DrawingAnnotation>) => 
    post<{ id: number }>(`/api/v1/drawings/${drawingId}/annotations`, data),
  updateAnnotation: (id: number, data: Partial<DrawingAnnotation>) =>
    patch(`/api/v1/annotations/${id}`, data),
  deleteAnnotation: (id: number) => del(`/api/v1/annotations/${id}`),
};

export const punchLists = {
  list: (projectId: string) => get<PunchList[]>(`/api/v1/projects/${projectId}/punch-lists`),
  get: (id: number) => get<PunchList>(`/api/v1/punch-lists/${id}`),
  create: (projectId: string, data: Partial<PunchList>) =>
    post<{ id: number }>(`/api/v1/projects/${projectId}/punch-lists`, data),
  update: (id: number, data: Partial<PunchList>) => patch<PunchList>(`/api/v1/punch-lists/${id}`, data),
  delete: (id: number) => del(`/api/v1/punch-lists/${id}`),
  summary: (id: number) => get<PunchListSummary>(`/api/v1/punch-lists/${id}/summary`),
  items: (id: number) => get<PunchItem[]>(`/api/v1/punch-lists/${id}/items`),
  createItem: (punchListId: number, data: Partial<PunchItem>) =>
    post<{ id: number; item_number: number }>(`/api/v1/punch-lists/${punchListId}/items`, data),
  getItem: (id: number) => get<PunchItem>(`/api/v1/punch-items/${id}`),
  updateItem: (id: number, data: Partial<PunchItem>) => patch<PunchItem>(`/api/v1/punch-items/${id}`, data),
  deleteItem: (id: number) => del(`/api/v1/punch-items/${id}`),
  completeItem: (id: number) => post(`/api/v1/punch-items/${id}/complete`),
  attachPhoto: (punchItemId: number, photoId: number, isBefore?: boolean, isAfter?: boolean) =>
    post(`/api/v1/punch-items/${punchItemId}/photos`, { photo_id: photoId, is_before: isBefore || false, is_after: isAfter || false }),
};

// ══════════════════════════════════════════════════════════════════════════════
// Sprint 12: Safety, Compliance, Warranty
// ══════════════════════════════════════════════════════════════════════════════

// ── Safety Incidents ─────────────────────────────────────────────────────────

export interface SafetyIncident {
  id: string;
  project_id: number;
  incident_date: string;
  incident_time?: string;
  location: string;
  incident_type: 'near_miss' | 'first_aid' | 'medical_treatment' | 'lost_time' | 'fatality' | 'property_damage' | 'environmental';
  description: string;
  root_cause: string;
  corrective_action: string;
  reported_by?: number;
  witnesses: string;
  injured_party: string;
  injury_description: string;
  days_lost: number;
  cnesst_reported: boolean;
  cnesst_report_number: string;
  status: 'open' | 'investigating' | 'corrective_action' | 'closed';
  closed_at?: string;
  created_at: string;
}

export const safetyIncidents = {
  list: (params?: { project_id?: number; project_public_id?: string; status?: string; type?: string }) => {
    const q = new URLSearchParams();
    if (params?.project_id) q.set('project_id', String(params.project_id));
    if (params?.project_public_id) q.set('project_public_id', params.project_public_id);
    if (params?.status) q.set('status', params.status);
    if (params?.type) q.set('type', params.type);
    return request<{ data: SafetyIncident[] }>(`/api/v1/safety/incidents${q.toString() ? '?' + q : ''}`);
  },
  create: (data: Partial<SafetyIncident>) =>
    request<SafetyIncident>('/api/v1/safety/incidents', { method: 'POST', ...body(data) }),
  get: (id: number) => request<SafetyIncident>(`/api/v1/safety/incidents/${id}`),
  update: (id: number, data: Partial<SafetyIncident>) =>
    request<SafetyIncident>(`/api/v1/safety/incidents/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/safety/incidents/${id}`, { method: 'DELETE' }),
};

// ── Toolbox Talks ────────────────────────────────────────────────────────────

export interface ToolboxTalk {
  id: string;
  project_id?: number;
  date: string;
  topic: string;
  description: string;
  presenter: string;
  attendees: Array<{ name: string; signature_url?: string }>;
  duration_minutes: number;
  created_at: string;
}

export const toolboxTalks = {
  list: (params?: { project_id?: number; project_public_id?: string }) => {
    const q = new URLSearchParams();
    if (params?.project_id) q.set('project_id', String(params.project_id));
    if (params?.project_public_id) q.set('project_public_id', params.project_public_id);
    return request<{ data: ToolboxTalk[] }>(`/api/v1/safety/toolbox-talks${q.toString() ? '?' + q : ''}`);
  },
  create: (data: Partial<ToolboxTalk>) =>
    request<ToolboxTalk>('/api/v1/safety/toolbox-talks', { method: 'POST', ...body(data) }),
  get: (id: number) => request<ToolboxTalk>(`/api/v1/safety/toolbox-talks/${id}`),
  update: (id: number, data: Partial<ToolboxTalk>) =>
    request<ToolboxTalk>(`/api/v1/safety/toolbox-talks/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/safety/toolbox-talks/${id}`, { method: 'DELETE' }),
};

// ── Safety Inspections ───────────────────────────────────────────────────────

export interface SafetyInspection {
  id: string;
  project_id: number;
  inspection_date: string;
  inspector_name: string;
  inspection_type: string;
  score?: number;
  findings: Array<{ category: string; finding: string; severity: string; corrective_action: string; due_date?: string; status: string }>;
  overall_status: 'pass' | 'pass_with_conditions' | 'fail';
  notes: string;
  next_inspection_date?: string;
  created_at: string;
}

export const safetyInspections = {
  list: (params?: { project_id?: number; project_public_id?: string; status?: string }) => {
    const q = new URLSearchParams();
    if (params?.project_id) q.set('project_id', String(params.project_id));
    if (params?.project_public_id) q.set('project_public_id', params.project_public_id);
    if (params?.status) q.set('status', params.status);
    return request<{ data: SafetyInspection[] }>(`/api/v1/safety/inspections${q.toString() ? '?' + q : ''}`);
  },
  create: (data: Partial<SafetyInspection>) =>
    request<SafetyInspection>('/api/v1/safety/inspections', { method: 'POST', ...body(data) }),
  get: (id: number) => request<SafetyInspection>(`/api/v1/safety/inspections/${id}`),
  update: (id: number, data: Partial<SafetyInspection>) =>
    request<SafetyInspection>(`/api/v1/safety/inspections/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/safety/inspections/${id}`, { method: 'DELETE' }),
};

// ── Safety Dashboard ─────────────────────────────────────────────────────────

export interface SafetyDashboard {
  days_since_last_incident: number;
  incidents_this_year: number;
  incidents_by_type: Record<string, number>;
  open_incidents: number;
  expiring_certifications: WorkerCertification[];
  recent_toolbox_talks: ToolboxTalk[];
}

export const safetyDashboard = {
  get: () => request<SafetyDashboard>('/api/v1/safety/dashboard'),
};

// ── Worker Certifications ────────────────────────────────────────────────────

export interface WorkerCertification {
  id: string;
  user_id?: number;
  contact_id?: number;
  certification_type: string;
  certification_number: string;
  issue_date?: string;
  expiry_date?: string;
  issuing_body: string;
  document_url: string;
  is_verified: boolean;
  created_at: string;
  user_name?: string;
  contact_name?: string;
}

export const certifications = {
  list: (params?: { user_id?: number; contact_id?: number; type?: string }) => {
    const q = new URLSearchParams();
    if (params?.user_id) q.set('user_id', String(params.user_id));
    if (params?.contact_id) q.set('contact_id', String(params.contact_id));
    if (params?.type) q.set('type', params.type);
    return request<{ data: WorkerCertification[] }>(`/api/v1/certifications${q.toString() ? '?' + q : ''}`);
  },
  create: (data: Partial<WorkerCertification>) =>
    request<WorkerCertification>('/api/v1/certifications', { method: 'POST', ...body(data) }),
  get: (id: number) => request<WorkerCertification>(`/api/v1/certifications/${id}`),
  update: (id: number, data: Partial<WorkerCertification>) =>
    request<WorkerCertification>(`/api/v1/certifications/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/certifications/${id}`, { method: 'DELETE' }),
  expiring: (days?: number) =>
    request<WorkerCertification[]>(`/api/v1/certifications/expiring${days ? '?days=' + days : ''}`),
};

// ── Warranties ───────────────────────────────────────────────────────────────

export interface Warranty {
  id: string;
  project_id: number;
  item_name: string;
  category: string;
  manufacturer: string;
  model_number: string;
  serial_number: string;
  vendor_contact_id?: number;
  purchase_date?: string;
  install_date?: string;
  warranty_start?: string;
  warranty_end?: string;
  warranty_type: string;
  coverage_details: string;
  document_url: string;
  registration_number: string;
  is_registered: boolean;
  notes: string;
  created_at: string;
  vendor_name?: string;
  project_name?: string;
}

export interface WarrantyClaim {
  id: string;
  warranty_id: number;
  claim_date: string;
  issue_description: string;
  resolution: string;
  claim_status: 'open' | 'submitted' | 'approved' | 'denied' | 'resolved';
  service_date?: string;
  cost_covered?: number;
  cost_out_of_pocket?: number;
  created_at: string;
}

export const warranties = {
  list: (params?: { project_id?: number; category?: string }) => {
    const q = new URLSearchParams();
    if (params?.project_id) q.set('project_id', String(params.project_id));
    if (params?.category) q.set('category', params.category);
    return request<{ data: Warranty[] }>(`/api/v1/warranties${q.toString() ? '?' + q : ''}`);
  },
  create: (data: Partial<Warranty>) =>
    request<Warranty>('/api/v1/warranties', { method: 'POST', ...body(data) }),
  get: (id: number) => request<Warranty>(`/api/v1/warranties/${id}`),
  update: (id: number, data: Partial<Warranty>) =>
    request<Warranty>(`/api/v1/warranties/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/warranties/${id}`, { method: 'DELETE' }),
  expiring: (days?: number) =>
    request<Warranty[]>(`/api/v1/warranties/expiring${days ? '?days=' + days : ''}`),
  active: () => request<Warranty[]>('/api/v1/warranties/active'),
  listClaims: (warrantyId: number) =>
    request<WarrantyClaim[]>(`/api/v1/warranties/${warrantyId}/claims`),
  createClaim: (warrantyId: number, data: Partial<WarrantyClaim>) =>
    request<WarrantyClaim>(`/api/v1/warranties/${warrantyId}/claims`, { method: 'POST', ...body(data) }),
  updateClaim: (claimId: number, data: Partial<WarrantyClaim>) =>
    request<WarrantyClaim>(`/api/v1/warranty-claims/${claimId}`, { method: 'PATCH', ...body(data) }),
  deleteClaim: (claimId: number) =>
    request<void>(`/api/v1/warranty-claims/${claimId}`, { method: 'DELETE' }),
};

// ── Lien Waivers ─────────────────────────────────────────────────────────────

export interface LienWaiver {
  id: string;
  project_id: number;
  subcontract_id?: number;
  vendor_contact_id?: number;
  waiver_type: 'conditional_progress' | 'unconditional_progress' | 'conditional_final' | 'unconditional_final';
  through_date: string;
  amount: number;
  received_date?: string;
  document_url: string;
  status: 'pending' | 'received' | 'verified';
  notes: string;
  created_at: string;
  vendor_name?: string;
}

export const lienWaivers = {
  list: (projectId: string, params?: { status?: string }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    return request<{ data: LienWaiver[] }>(`/api/v1/projects/${projectId}/lien-waivers${q.toString() ? '?' + q : ''}`);
  },
  create: (projectId: string, data: Partial<LienWaiver>) =>
    request<LienWaiver>(`/api/v1/projects/${projectId}/lien-waivers`, { method: 'POST', ...body(data) }),
  get: (id: number) => request<LienWaiver>(`/api/v1/lien-waivers/${id}`),
  update: (id: number, data: Partial<LienWaiver>) =>
    request<LienWaiver>(`/api/v1/lien-waivers/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/lien-waivers/${id}`, { method: 'DELETE' }),
  missing: (projectId: string) =>
    request<Array<{ subcontract_id: number; vendor_contact_id: number; vendor_name: string; billed_amount: number; waived_amount: number; missing_amount: number }>>(`/api/v1/projects/${projectId}/lien-waivers/missing`),
};

// ── Compliance Calendar ──────────────────────────────────────────────────────

export interface ComplianceItem {
  type: string;
  id: string;
  title: string;
  date: string;
  project_id?: number;
  project_name?: string;
  status: string;
  details?: string;
}

export const complianceCalendar = {
  get: (start: string, end: string) =>
    request<ComplianceItem[]>(`/api/v1/compliance/calendar?start=${start}&end=${end}`),
};

// ── GCR Warranty (Quebec) ────────────────────────────────────────────────────

export interface GCRWarrantyInfo {
  project_id: number;
  gcr_registration_number: string;
  gcr_registration_date?: string;
  warranty_1_year_end?: string;
  warranty_3_year_end?: string;
  warranty_5_year_end?: string;
  completion_date?: string;
}

export const gcrWarranty = {
  get: (projectId: string) => request<GCRWarrantyInfo>(`/api/v1/projects/${projectId}/gcr-warranty`),
  register: (projectId: string, data: { gcr_registration_number: string; gcr_registration_date?: string; completion_date?: string }) =>
    request<GCRWarrantyInfo>(`/api/v1/projects/${projectId}/gcr-register`, { method: 'POST', ...body(data) }),
};

// ── Sprint 14: Estimate Templates, Assemblies, Proposals ──────────────────────

export interface EstimateTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  line_items: unknown[];
  default_markup_pct: number;
  is_active: boolean;
  created_by?: number;
  created_at: string;
  updated_at: string;
}

export const estimateTemplates = {
  list: (params?: { category?: string; active?: boolean }) => {
    const q = new URLSearchParams();
    if (params?.category) q.set('category', params.category);
    if (params?.active !== undefined) q.set('active', String(params.active));
    return request<PagedResponse<EstimateTemplate>>(`/api/v1/estimate-templates${q.toString() ? '?' + q : ''}`);
  },
  get: (id: number) => request<EstimateTemplate>(`/api/v1/estimate-templates/${id}`),
  create: (data: Partial<EstimateTemplate>) =>
    request<EstimateTemplate>('/api/v1/estimate-templates', { method: 'POST', ...body(data) }),
  update: (id: number, data: Partial<EstimateTemplate>) =>
    request<EstimateTemplate>(`/api/v1/estimate-templates/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/estimate-templates/${id}`, { method: 'DELETE' }),
  createEstimate: (templateId: number, data: { project_id?: number; contact_id?: number; title?: string }) =>
    request<Estimate>(`/api/v1/estimate-templates/${templateId}/create-estimate`, { method: 'POST', ...body(data) }),
};

export interface Assembly {
  id: string;
  name: string;
  category: string;
  description: string;
  default_markup_pct: number;
  labor_hours: number;
  labor_rate: number;
  is_active: boolean;
  visible_in_list: boolean;
  cost_code_id?: number;
  total_material_cost?: number;
  total_labor_cost?: number;
  total_cost?: number;
  items?: AssemblyItem[];
  created_at: string;
  updated_at: string;
}

export interface AssemblyItem {
  id: string;
  assembly_id: number;
  catalog_item_id?: number;
  name: string;
  description: string;
  quantity: number;
  unit: string;
  unit_cost_override?: number;
  sort_order: number;
  cost_type: string;
  quantity_formula?: string;
  markup_pct: number;
  tax_pct: number;
  sub_vendor_id?: number;
  catalog_item_name?: string;
  unit_cost: number;
  line_total: number;
}

export const assemblies = {
  list: (params?: { category?: string; active?: boolean; visible?: boolean }) => {
    const q = new URLSearchParams();
    if (params?.category) q.set('category', params.category);
    if (params?.active !== undefined) q.set('active', String(params.active));
    if (params?.visible !== undefined) q.set('visible', String(params.visible));
    return request<PagedResponse<Assembly>>(`/api/v1/cost-catalog/assemblies${q.toString() ? '?' + q : ''}`);
  },
  get: (id: number) => request<Assembly>(`/api/v1/cost-catalog/assemblies/${id}`),
  create: (data: Partial<Assembly>) =>
    request<Assembly>('/api/v1/cost-catalog/assemblies', { method: 'POST', ...body(data) }),
  update: (id: number, data: Partial<Assembly>) =>
    request<Assembly>(`/api/v1/cost-catalog/assemblies/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/cost-catalog/assemblies/${id}`, { method: 'DELETE' }),
  listItems: (id: number) => request<AssemblyItem[]>(`/api/v1/cost-catalog/assemblies/${id}/items`),
  createItem: (assemblyId: number, data: Partial<AssemblyItem>) =>
    request<AssemblyItem>(`/api/v1/cost-catalog/assemblies/${assemblyId}/items`, { method: 'POST', ...body(data) }),
  updateItem: (assemblyId: number, itemId: number, data: Partial<AssemblyItem>) =>
    request<AssemblyItem>(`/api/v1/cost-catalog/assemblies/${assemblyId}/items/${itemId}`, { method: 'PATCH', ...body(data) }),
  deleteItem: (assemblyId: number, itemId: number) =>
    request<void>(`/api/v1/cost-catalog/assemblies/${assemblyId}/items/${itemId}`, { method: 'DELETE' }),
  calculate: (id: number) =>
    request<{ assembly_id: number; assembly_name: string; assembly_qty: number; items: AssemblyItem[]; material_cost: number; labor_hours: number; labor_rate: number; labor_cost: number; subtotal: number; markup_pct: number; markup_amount: number; total: number }>(`/api/v1/cost-catalog/assemblies/${id}/calculate`),
  calculateWithQty: (id: number, qty: number) =>
    request<{ assembly_id: number; assembly_name: string; assembly_qty: number; items: AssemblyItem[]; material_cost: number; labor_hours: number; labor_rate: number; labor_cost: number; subtotal: number; markup_pct: number; markup_amount: number; total: number }>(`/api/v1/cost-catalog/assemblies/${id}/calculate?qty=${qty}`),
};

export interface LaborRate {
  id: string;
  name: string;
  role: string;
  hourly_rate: number;
  overtime_rate?: number;
  burden_pct: number;
  effective_date: string;
  is_active: boolean;
  fully_burdened_rate: number;
  created_at: string;
}

export const laborRates = {
  list: (params?: { role?: string; active?: boolean }) => {
    const q = new URLSearchParams();
    if (params?.role) q.set('role', params.role);
    if (params?.active !== undefined) q.set('active', String(params.active));
    return request<PagedResponse<LaborRate>>(`/api/v1/labor-rates${q.toString() ? '?' + q : ''}`);
  },
  get: (id: number) => request<LaborRate>(`/api/v1/labor-rates/${id}`),
  create: (data: Partial<LaborRate>) =>
    request<LaborRate>('/api/v1/labor-rates', { method: 'POST', ...body(data) }),
  update: (id: number, data: Partial<LaborRate>) =>
    request<LaborRate>(`/api/v1/labor-rates/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/labor-rates/${id}`, { method: 'DELETE' }),
};

export interface CrewRate {
  id: string;
  name: string;
  description: string;
  members: Array<{ role: string; count: number; rate_id: number }>;
  daily_rate: number;
  created_at: string;
  updated_at: string;
}

export const crewRates = {
  list: () => request<PagedResponse<CrewRate>>('/api/v1/crew-rates'),
  get: (id: number) => request<CrewRate>(`/api/v1/crew-rates/${id}`),
  create: (data: Partial<CrewRate>) =>
    request<CrewRate>('/api/v1/crew-rates', { method: 'POST', ...body(data) }),
  update: (id: number, data: Partial<CrewRate>) =>
    request<CrewRate>(`/api/v1/crew-rates/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/crew-rates/${id}`, { method: 'DELETE' }),
};

export interface Proposal {
  id: string;
  project_id?: number;
  estimate_id?: number;
  proposal_number: string;
  title: string;
  client_contact_id?: number;
  introduction: string;
  scope_of_work: string;
  exclusions: string;
  assumptions: string;
  timeline: string;
  payment_terms: string;
  total_amount: number;
  valid_until?: string;
  status: 'draft' | 'sent' | 'viewed' | 'accepted' | 'declined' | 'expired';
  view_token?: string;
  sent_at?: string;
  viewed_at?: string;
  accepted_at?: string;
  declined_at?: string;
  client_signature: string;
  client_signed_at?: string;
  decline_reason: string;
  created_at: string;
  updated_at: string;
  project_name?: string;
  client_name?: string;
  client_email?: string;
  sections?: ProposalSection[];
  // Document view tracking
  last_viewer_email?: string;
  last_viewer_name?: string;
  last_viewed_at?: string;
  total_views?: number;
}

export interface ProposalSection {
  id: string;
  proposal_id: number;
  title: string;
  content: string;
  show_pricing: boolean;
  line_items: LineItem[];  // simple pricing lines (description, qty, unit, unit_price, total)
  subtotal: number;
  sort_order: number;
}

export const proposals = {
  list: (params?: { project_id?: number | string; status?: string }) => {
    const q = new URLSearchParams();
    if (params?.project_id) q.set('project_id', String(params.project_id));
    if (params?.status) q.set('status', params.status);
    return request<PagedResponse<Proposal>>(`/api/v1/proposals${q.toString() ? '?' + q : ''}`);
  },
  get: (id: string) => request<Proposal>(`/api/v1/proposals/${id}`),
  create: (data: Partial<Proposal>) =>
    request<Proposal>('/api/v1/proposals', { method: 'POST', ...body(data) }),
  update: (id: string, data: Partial<Proposal>) =>
    request<Proposal>(`/api/v1/proposals/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: string) => request<void>(`/api/v1/proposals/${id}`, { method: 'DELETE' }),
  createFromEstimate: (estimateId: string) =>
    request<Proposal>(`/api/v1/estimates/${estimateId}/create-proposal`, { method: 'POST' }),
  send: (id: string, skipExclusionsCheck?: boolean) =>
    request<Proposal>(`/api/v1/proposals/${id}/send${skipExclusionsCheck ? '?skip_exclusions_check=true' : ''}`, { method: 'POST' }),
  extend: (id: string, validUntil: string) =>
    request<{ extended: boolean; valid_until: string }>(`/api/v1/proposals/${id}/extend`, { method: 'PATCH', ...body({ valid_until: validUntil }) }),
  accept: (id: string, signature: string) =>
    request<Proposal>(`/api/v1/proposals/${id}/accept`, { method: 'POST', ...body({ signature }) }),
  decline: (id: string, reason?: string) =>
    request<Proposal>(`/api/v1/proposals/${id}/decline`, { method: 'POST', ...body({ reason }) }),
  listSections: (id: string) => request<ProposalSection[]>(`/api/v1/proposals/${id}/sections`),
  createSection: (proposalId: string, data: Partial<ProposalSection>) =>
    request<ProposalSection>(`/api/v1/proposals/${proposalId}/sections`, { method: 'POST', ...body(data) }),
  updateSection: (proposalId: string, sectionId: number, data: Partial<ProposalSection>) =>
    request<ProposalSection>(`/api/v1/proposals/${proposalId}/sections/${sectionId}`, { method: 'PATCH', ...body(data) }),
  deleteSection: (proposalId: string, sectionId: number) =>
    request<void>(`/api/v1/proposals/${proposalId}/sections/${sectionId}`, { method: 'DELETE' }),
};

export interface EstimateHistory {
  id: string;
  estimate_id: number;
  version: number;
  snapshot: unknown;
  changed_by?: number;
  change_notes: string;
  created_at: string;
  changed_by_name?: string;
}

export const estimateVersions = {
  list: (estimateId: string) =>
    request<EstimateVersion[]>(`/api/v1/estimates/${estimateId}/versions`),
  get: (estimateId: string, versionId: number) =>
    request<{ version: EstimateVersion; line_items: EstimateLineItem[] }>(`/api/v1/estimates/${estimateId}/versions/${versionId}`),
  create: (estimateId: string, data: { version_name: string; set_working?: boolean; source_version_id?: number; blank?: boolean }) =>
    request<EstimateVersion>(`/api/v1/estimates/${estimateId}/versions`, { method: 'POST', ...body(data) }),
  update: (estimateId: string, versionId: number, data: { version_name?: string }) =>
    request<EstimateVersion>(`/api/v1/estimates/${estimateId}/versions/${versionId}`, { method: 'PATCH', ...body(data) }),
  setWorking: (estimateId: string, versionId: number) =>
    request<{ ok: string }>(`/api/v1/estimates/${estimateId}/versions/${versionId}/set-working`, { method: 'PATCH' }),
  restore: (estimateId: string, versionId: number) =>
    request<{ ok: string }>(`/api/v1/estimates/${estimateId}/versions/${versionId}/restore`, { method: 'POST' }),
}

export const estimateVersioning = {
  createRevision: (estimateId: string, changeNotes?: string) =>
    request<{ estimate: Estimate; new_version: number; message: string }>(`/api/v1/estimates/${estimateId}/create-revision`, { method: 'POST', ...body({ change_notes: changeNotes || '' }) }),
  getHistory: (estimateId: string) =>
    request<EstimateHistory[]>(`/api/v1/estimates/${estimateId}/history`),
  applyTemplate: (estimateId: string, templateId: number, merge?: boolean) =>
    request<Estimate>(`/api/v1/estimates/${estimateId}/apply-template`, { method: 'POST', ...body({ template_id: templateId, merge: merge || false }) }),
  saveAsTemplate: (estimateId: string, name: string, description?: string, category?: string) =>
    request<EstimateTemplate>(`/api/v1/estimates/${estimateId}/save-as-template`, { method: 'POST', ...body({ name, description: description || '', category: category || '' }) }),
};

export interface QuickEstimateRequest {
  category: string;
  square_footage: number;
  quality_level: 'standard' | 'mid' | 'premium';
  description?: string;
}

export interface QuickEstimateResult {
  category: string;
  square_footage: number;
  quality_level: string;
  rate_per_sqft: number;
  base_cost: number;
  markup_pct: number;
  markup_amount: number;
  total_estimate: number;
  description?: string;
}

export interface QuickEstimateSettings {
  id: string;
  category: string;
  template_id?: number;
  base_sqft_rate_standard: number;
  base_sqft_rate_mid: number;
  base_sqft_rate_premium: number;
  default_markup_pct: number;
  created_at: string;
  updated_at: string;
}

export const quickEstimate = {
  calculate: (data: QuickEstimateRequest) =>
    request<QuickEstimateResult>('/api/v1/quick-estimate', { method: 'POST', ...body(data) }),
  convertToEstimate: (result: QuickEstimateResult & { project_id?: number; contact_id?: number; title?: string }) =>
    request<Estimate>('/api/v1/quick-estimate/convert', { method: 'POST', ...body(result) }),
  getSettings: () => request<QuickEstimateSettings[]>('/api/v1/quick-estimate/settings'),
  updateSettings: (category: string, data: Partial<QuickEstimateSettings>) =>
    request<QuickEstimateSettings>(`/api/v1/quick-estimate/settings/${category}`, { method: 'PATCH', ...body(data) }),
};

// ══════════════════════════════════════════════════════════════════════════════
// Sprint 16: Equipment, Vehicles, Materials, Proposals
// ══════════════════════════════════════════════════════════════════════════════

// ── Equipment ─────────────────────────────────────────────────────────────────

export interface Equipment {
  id: string;
  name: string;
  type: string;
  /** Canonical API field (backend column `equipment_type`); `type` is retained for older list responses/callers. */
  equipment_type?: string;
  make: string;
  model: string;
  serial_number: string;
  asset_tag: string;
  status: 'available' | 'in_use' | 'maintenance' | 'retired' | 'rented';
  ownership: 'owned' | 'rented';
  location: string;
  assigned_to_project_id?: number;
  assigned_to_project_name?: string;
  assigned_to_user_id?: number;
  assigned_to_user_name?: string;
  purchase_date?: string;
  purchase_price?: number;
  rental_rate_daily?: number;
  rental_start?: string;
  rental_end?: string;
  last_maintenance_date?: string;
  next_maintenance_date?: string;
  notes?: string;
  created_at: string;
}

export interface EquipmentMaintenance {
  id: string;
  equipment_id: number;
  maintenance_type: string;
  description: string;
  performed_by: string;
  cost?: number;
  next_due_date?: string;
  created_at: string;
}

export const equipment = {
  list: () => request<{ data: Equipment[] }>('/api/v1/equipment'),
  get: (id: number) => request<Equipment>(`/api/v1/equipment/${id}`),
  create: (data: Partial<Equipment>) =>
    request<Equipment>('/api/v1/equipment', { method: 'POST', ...body(data) }),
  update: (id: number, data: Partial<Equipment>) =>
    request<Equipment>(`/api/v1/equipment/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/equipment/${id}`, { method: 'DELETE' }),
  duplicate: (id: number) => request<Equipment>(`/api/v1/equipment/${id}/duplicate`, { method: 'POST' }),
  assign: (id: number, data: { project_id: number }) =>
    request<Equipment>(`/api/v1/equipment/${id}/assign`, { method: 'POST', ...body(data) }),
  returnEquipment: (id: number) =>
    request<Equipment>(`/api/v1/equipment/${id}/return`, { method: 'POST', ...body({}) }),
  checkout: (id: number) =>
    request<Equipment>(`/api/v1/equipment/${id}/checkout`, { method: 'POST', ...body({}) }),
  checkin: (id: number) =>
    request<Equipment>(`/api/v1/equipment/${id}/checkin`, { method: 'POST', ...body({}) }),
  maintenance: (id: number) =>
    request<{ data: EquipmentMaintenance[] }>(`/api/v1/equipment/${id}/maintenance`),
  logMaintenance: (id: number, data: Partial<EquipmentMaintenance>) =>
    request<EquipmentMaintenance>(`/api/v1/equipment/${id}/maintenance`, { method: 'POST', ...body(data) }),
  rentals: () => request<{ data: Equipment[] }>('/api/v1/equipment/rentals'),
  maintenanceDue: () => request<{ data: Equipment[] }>('/api/v1/equipment/maintenance-due'),
};

// ── Vehicles ──────────────────────────────────────────────────────────────────

export interface Vehicle {
  id: string;
  name: string;
  type: string;
  license_plate: string;
  vin: string;
  make: string;
  model: string;
  year: number;
  color: string;
  assigned_to_user_id?: number;
  assigned_to_user_name?: string;
  odometer: number;
  fuel_type: string;
  status: 'available' | 'in_use' | 'maintenance' | 'out_of_service';
  registration_expiry?: string;
  insurance_expiry?: string;
  last_service_date?: string;
  next_service_due?: string;
  notes?: string;
  created_at: string;
}

export interface VehicleLog {
  id: string;
  vehicle_id: number;
  log_type: string;
  date: string;
  start_odometer?: number;
  end_odometer?: number;
  distance?: number;
  fuel_gallons?: number;
  fuel_cost?: number;
  description: string;
  driver_name?: string;
  created_at: string;
}

export interface VehicleSummary {
  total_miles: number;
  total_fuel_gallons: number;
  total_fuel_cost: number;
  avg_mpg: number;
  trips_count: number;
}

export const vehicles = {
  list: () => request<{ data: Vehicle[] }>('/api/v1/vehicles'),
  get: (id: number) => request<Vehicle>(`/api/v1/vehicles/${id}`),
  create: (data: Partial<Vehicle>) =>
    request<Vehicle>('/api/v1/vehicles', { method: 'POST', ...body(data) }),
  update: (id: number, data: Partial<Vehicle>) =>
    request<Vehicle>(`/api/v1/vehicles/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/vehicles/${id}`, { method: 'DELETE' }),
  logs: (id: number) => request<{ data: VehicleLog[] }>(`/api/v1/vehicles/${id}/logs`),
  addLog: (id: number, data: Partial<VehicleLog>) =>
    request<VehicleLog>(`/api/v1/vehicles/${id}/logs`, { method: 'POST', ...body(data) }),
  summary: (id: number) => request<VehicleSummary>(`/api/v1/vehicles/${id}/summary`),
  expiryAlerts: () => request<{ data: Vehicle[] }>('/api/v1/vehicles/expiry-alerts'),
};

// ── Materials / Inventory ─────────────────────────────────────────────────────

export interface MaterialOrder {
  id: string;
  vendor_id?: number;
  vendor_name?: string;
  project_id?: number;
  project_name?: string;
  order_date: string;
  expected_delivery?: string;
  status: 'draft' | 'ordered' | 'partial' | 'received' | 'cancelled';
  total: number;
  items: { name: string; quantity: number; unit: string; unit_price: number }[];
  notes?: string;
  created_at: string;
}

export interface InventoryItem {
  id: string;
  name: string;
  sku?: string;
  category: string;
  location: string;
  quantity: number;
  min_quantity: number;
  unit: string;
  unit_cost: number;
  total_value: number;
  last_restocked?: string;
  notes?: string;
}

export interface MaterialUsageEntry {
  id: string;
  inventory_id: number;
  item_name: string;
  quantity: number;
  project_id?: number;
  project_name?: string;
  used_by?: number;
  user_name?: string;
  usage_date: string;
  notes?: string;
}

export const materialOrders = {
  list: () => request<{ data: MaterialOrder[] }>('/api/v1/material-orders'),
  get: (id: number) => request<MaterialOrder>(`/api/v1/material-orders/${id}`),
  create: (data: Partial<MaterialOrder>) =>
    request<MaterialOrder>('/api/v1/material-orders', { method: 'POST', ...body(data) }),
  update: (id: number, data: Partial<MaterialOrder>) =>
    request<MaterialOrder>(`/api/v1/material-orders/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/material-orders/${id}`, { method: 'DELETE' }),
  receive: (id: number) =>
    request<MaterialOrder>(`/api/v1/material-orders/${id}/receive`, { method: 'POST', ...body({}) }),
};

export const inventoryApi = {
  list: () => request<{ data: InventoryItem[] }>('/api/v1/inventory'),
  get: (id: number) => request<InventoryItem>(`/api/v1/inventory/${id}`),
  create: (data: Partial<InventoryItem>) =>
    request<InventoryItem>('/api/v1/inventory', { method: 'POST', ...body(data) }),
  update: (id: number, data: Partial<InventoryItem>) =>
    request<InventoryItem>(`/api/v1/inventory/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) => request<void>(`/api/v1/inventory/${id}`, { method: 'DELETE' }),
  adjust: (id: number, data: { quantity: number; reason?: string }) =>
    request<InventoryItem>(`/api/v1/inventory/${id}/adjust`, { method: 'POST', ...body(data) }),
  use: (id: number, data: { quantity: number; project_id?: number; notes?: string }) =>
    request<MaterialUsageEntry>(`/api/v1/inventory/${id}/use`, { method: 'POST', ...body(data) }),
  lowStock: () => request<{ data: InventoryItem[] }>('/api/v1/inventory/low-stock'),
};

export const materialUsage = {
  list: () => request<{ data: MaterialUsageEntry[] }>('/api/v1/material-usage'),
};

// ── Subcontracts V2 ───────────────────────────────────────────────────────────

export interface SubcontractV2 {
  id: string;
  project_id: number;
  project_name?: string;
  vendor_contact_id?: number;
  vendor_name?: string;
  contract_number: string;
  scope_summary: string;
  original_amount: number;
  approved_changes: number;
  current_amount: number;
  billed_amount: number;
  paid_amount: number;
  retention_pct: number;
  retention_held: number;
  status: 'draft' | 'pending' | 'active' | 'complete' | 'terminated';
  start_date?: string;
  end_date?: string;
  signed_date?: string;
  notes?: string;
  created_at: string;
}

export interface SubcontractChange {
  id: string;
  subcontract_id: number;
  sco_number: string;
  description: string;
  amount: number;
  status: 'pending' | 'approved' | 'rejected';
  requested_date: string;
  approved_date?: string;
  notes?: string;
}

export interface SubcontractBilling {
  id: string;
  subcontract_id: number;
  billing_number: number;
  billing_date: string;
  period_start?: string;
  period_end?: string;
  work_completed: number;
  materials_stored: number;
  gross_amount: number;
  retention_amount: number;
  net_amount: number;
  status: 'draft' | 'submitted' | 'approved' | 'paid';
  paid_date?: string;
  notes?: string;
}

export const subcontractsV2 = {
  list: () => request<{ data: SubcontractV2[] }>('/api/v1/subcontracts-v2'),
  get: (id: number) => request<SubcontractV2>(`/api/v1/subcontracts-v2/${id}`),
  create: (data: Partial<SubcontractV2>) =>
    request<SubcontractV2>('/api/v1/subcontracts-v2', { method: 'POST', ...body(data) }),
  update: (id: number, data: Partial<SubcontractV2>) =>
    request<SubcontractV2>(`/api/v1/subcontracts-v2/${id}`, { method: 'PATCH', ...body(data) }),
  changes: (id: number) =>
    request<{ data: SubcontractChange[] }>(`/api/v1/subcontracts/${id}/changes`),
  addChange: (id: number, data: Partial<SubcontractChange>) =>
    request<SubcontractChange>(`/api/v1/subcontracts/${id}/changes`, { method: 'POST', ...body(data) }),
  billings: (id: number) =>
    request<{ data: SubcontractBilling[] }>(`/api/v1/subcontracts/${id}/billings`),
  addBilling: (id: number, data: Partial<SubcontractBilling>) =>
    request<SubcontractBilling>(`/api/v1/subcontracts/${id}/billings`, { method: 'POST', ...body(data) }),
};


// ── Activity Log ──────────────────────────────────────────────────────────────

export interface ActivityEntry {
  id: string;
  user_id?: number;
  user_name?: string;
  user_email?: string;
  action: 'create' | 'update' | 'delete' | 'view' | 'login' | 'export';
  entity_type: string;
  entity_id?: number;
  entity_name?: string;
  changes?: { field: string; old_value: unknown; new_value: unknown }[];
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export const activityLog = {
  list: (params?: { user_id?: number; action?: string; entity_type?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.user_id) q.set('user_id', String(params.user_id));
    if (params?.action) q.set('action', params.action);
    if (params?.entity_type) q.set('entity_type', params.entity_type);
    if (params?.limit) q.set('limit', String(params.limit));
    return request<{ data: ActivityEntry[] }>(`/api/v1/admin/activity${q.toString() ? '?' + q : ''}`);
  },
};

// ── Comments ──────────────────────────────────────────────────────────────────

export const comments = {
  counts: (entityType: string, ids: number[]): Promise<{ counts: Record<string, number> }> =>
    request<{ counts: Record<string, number> }>(`/api/v1/comments/counts?entity_type=${entityType}&ids=${ids.join(',')}`),
};

// ── User Profile ──────────────────────────────────────────────────────────────

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  role: string;
  phone: string;
  title: string;
  created_at: string;
  // Extended profile fields
  avatar_url?: string;
  timezone?: string;
  notification_prefs?: {
    task_assigned: boolean;
    rfi_answered: boolean;
    invoice_due: boolean;
    daily_summary: boolean;
  };
  is_google_connected?: boolean;
  language_preference?: string | null;
  theme_preference?: string;
}

export interface NotificationPrefs {
  task_assigned: boolean;
  rfi_answered: boolean;
  invoice_due: boolean;
  daily_summary: boolean;
}

export const userProfile = {
  get: () => request<UserProfile>('/api/v1/me'),
  update: (data: Partial<Pick<UserProfile, 'name' | 'phone' | 'title'>>) =>
    request<UserProfile>('/api/v1/me', { method: 'PATCH', ...body(data) }),
};

// ── Profile (new endpoints) ────────────────────────────────────────────────────

export const profile = {
  get: () => request<UserProfile>('/api/v1/profile'),
  update: (data: { name: string; timezone?: string; notification_prefs?: NotificationPrefs }) =>
    request<UserProfile>('/api/v1/profile', { method: 'PUT', ...body(data) }),
  changePassword: (data: { current_password: string; new_password: string }) =>
    request<{ success: boolean }>('/api/v1/profile/change-password', { method: 'POST', ...body(data) }),
  updateLanguage: (language: string | null) =>
    request<{ language: string | null }>('/api/v1/profile/language', { method: 'PATCH', ...body({ language }) }),
  uploadAvatar: async (file: File): Promise<{ avatar_url: string }> => {
    const token = getToken() || ''
    const form = new FormData()
    form.append('avatar', file)
    const res = await fetch('/api/v1/profile/avatar', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Upload failed' }))
      throw new Error(err.error || 'Upload failed')
    }
    return res.json()
  },
};

// ── Notifications (typed) ─────────────────────────────────────────────────────

export interface NotificationItem {
  id: string;
  user_id: number;
  event_type: string;
  title: string;
  body: string;
  entity_type: string;
  entity_id: number | null;
  read_at: string | null;
  created_at: string;
}

export interface NotifPreference {
  id: string;
  user_id: number;
  event_type: string;
  email: boolean;
  in_app: boolean;
}

export const notifications = {
  list: (unreadOnly?: boolean) =>
    request<{ items: NotificationItem[]; unread_count: number }>(
      `/api/v1/notifications${unreadOnly ? '?unread=true' : ''}`
    ),
  markRead: (id: number) =>
    request<{ ok: boolean }>(`/api/v1/notifications/${id}/read`, { method: 'PATCH' }),
  markAllRead: () =>
    request<{ updated: number }>('/api/v1/notifications/read-all', { method: 'POST' }),
  getPrefs: () => request<NotifPreference[]>('/api/v1/notification-preferences'),
  updatePrefs: (prefs: Array<{ event_type: string; email: boolean; in_app: boolean }>) =>
    request<{ updated: number }>('/api/v1/notification-preferences', {
      method: 'PATCH',
      ...body({ preferences: prefs }),
    }),
  getVapidKey: () => request<{ public_key: string }>('/api/v1/push/vapid-key'),
  subscribePush: (sub: { endpoint: string; p256dh: string; auth: string }) =>
    request<{ ok: boolean }>('/api/v1/push/subscribe', { method: 'POST', ...body(sub) }),
  unsubscribePush: (sub: { endpoint: string; p256dh: string; auth: string }) =>
    request<{ ok: boolean }>('/api/v1/push/subscribe', { method: 'DELETE', ...body(sub) }),
};

// ── Dashboard Summary ─────────────────────────────────────────────────────────

export interface DashboardSummary {
  active_projects: number;
  open_invoices_amount: number;
  open_invoices_count: number;
  overdue_tasks: number;
  unread_rfis: number;
  unread_notifications: number;
  upcoming_tasks: Array<{
    id: string;
    title: string;
    project_id: number | null;
    project_name: string;
    status: string;
    due_date: string;
  }>;
  recent_activity: Array<{
    type: string;
    title: string;
    timestamp: string;
  }>;
}

export const dashboardSummary = {
  get: () => request<DashboardSummary>('/api/v1/dashboard/summary'),
};

// ── Users (for assignee pickers) ─────────────────────────────────────────────

export interface AppUser {
  id: string;
  email: string;
  name: string;
  role: string;
  created_at: string;
}

export const users = {
  // list() returns only active, non-agent users (defensive frontend filter matches backend)
  list: async (): Promise<AppUser[]> => {
    const result = await request<AppUser[]>('/api/v1/users');
    const arr: AppUser[] = Array.isArray(result) ? result : ((result as any)?.data ?? []);
    return arr.filter((u: any) =>
      u.deleted_at == null &&
      u.role !== 'agent' &&
      !u.is_agent &&
      u.account_type !== 'agent' &&
      u.account_type !== 'system'
    );
  },
};

// ── Assignable People (users + external contacts) ──────────────────────────
export interface AssignablePerson {
  id: number;              // negative = contact ID
  name: string;
  email: string;
  person_type: string;     // 'user' | 'client' | 'subcontractor' | 'vendor'
  is_user: boolean;
  contact_id?: number;
  invite_pending?: boolean;
  /** Contact's own company name (subs/vendors) — see contacts.company. */
  company?: string;
  /** Contact's trade (subs) — see contacts.trade. */
  trade?: string;
}

export const assignablePeople = {
  // No params: full company directory (back-compat). `projectId` alone: the
  // unsearched view scoped to that project's roster, annotated with
  // project_access_ids. `q`: server-side search across the whole company
  // directory (reaches beyond the active project), bounded by `limit`
  // (server default 30, max 50) — never client-filter a pre-fetched list.
  // projectId accepts either the internal numeric project id or its public_id
  // (UUID) string — the backend resolves both via ResolveProjectPublicID.
  list: (params?: { projectId?: number | string; q?: string; limit?: number }): Promise<AssignablePerson[] | { people: AssignablePerson[]; project_access_ids: number[] }> => {
    const sp = new URLSearchParams();
    if (params?.projectId) sp.set('project_id', String(params.projectId));
    if (params?.q) sp.set('q', params.q);
    if (params?.limit) sp.set('limit', String(params.limit));
    const qs = sp.toString();
    return request(`/api/v1/assignable-people${qs ? '?' + qs : ''}`);
  },
};

// ── Task grant-access (#804) ────────────────────────────────────────────────
export interface TaskGrantResult {
  grant_id: number;
  contact_id: number;
  status: 'active' | 'pending';
}

export const taskGrants = {
  // Issues (or reuses) a task-scoped access grant for a contact who isn't on
  // the task's project. 'active' = the contact already had a platform
  // identity and is now a live assignee; 'pending' = a brand-new contact was
  // just invited and will become a live assignee once they accept.
  grant: (taskId: string, contactId: number): Promise<TaskGrantResult> =>
    post(`/tasks/${taskId}/grant-access`, { contact_id: contactId }),
};

// ── Contact ↔ Project access ───────────────────────────────────────────────
export interface ContactProjectAccessRow {
  project_id: number;
  project_name: string;
  status: string;
  has_access: boolean;
}
export interface ContactProjectAccessResponse {
  data: ContactProjectAccessRow[];
  has_linked_user: boolean;
  user_role: string;
  all_projects_access: boolean;
  is_admin_role: boolean;
}

export const contactProjectAccess = {
  /** GET /api/v1/contacts/{id}/project-access — per-project access for a contact. */
  get: (contactId: string | number) =>
    request<ContactProjectAccessResponse>(`/api/v1/contacts/${contactId}/project-access`),
  /** PATCH the explicit project_ids list (full desired set — server diffs add/remove). */
  setProjects: (contactId: string | number, projectIds: number[]) =>
    request<{ status: string }>(`/api/v1/contacts/${contactId}/project-access`, {
      method: 'PATCH',
      ...body({ project_ids: projectIds }),
    }),
  /** Grant a single project: reads current access, appends, PATCHes the union. */
  grant: async (contactId: string | number, projectId: number) => {
    const cur = await request<ContactProjectAccessResponse>(`/api/v1/contacts/${contactId}/project-access`);
    const current = (cur.data || []).filter(r => r.has_access).map(r => r.project_id);
    const next = Array.from(new Set([...current, projectId]));
    return request<{ status: string }>(`/api/v1/contacts/${contactId}/project-access`, {
      method: 'PATCH',
      ...body({ project_ids: next }),
    });
  },
};

// ── Auth: Password Reset ──────────────────────────────────────────────────────

export const authReset = {
  forgotPassword: (email: string) =>
    request<{ message: string }>('/api/v1/auth/forgot-password', { method: 'POST', ...body({ email }) }),
  validateToken: (token: string) =>
    request<{ valid: boolean }>(`/api/v1/auth/reset-password?token=${encodeURIComponent(token)}`),
  resetPassword: (token: string, password: string) =>
    request<{ message: string }>('/api/v1/auth/reset-password', { method: 'POST', ...body({ token, password }) }),
};

// ── Vendor Portal (document token signing) ───────────────────────────────────

export interface DocumentToken {
  id: string;
  token: string;
  entity_type: 'purchase_order' | 'change_order' | 'subcontract' | 'submittal_reviewer' | 'rfi';
  entity_id: number;
  recipient_email: string;
  recipient_name: string;
  status: 'pending' | 'signed' | 'declined' | 'expired';
  created_at: string;
  expires_at?: string;
  signed_at?: string;
  declined_at?: string;
  /** submittal_reviewer/rfi only — 3-way decision or RFI response text. */
  decision?: string;
  notes?: string;
}

export const vendorPortal = {
  // recipient_email/name are ignored (and unnecessary) for entity_type
  // submittal_reviewer/rfi — the backend always resolves the invite to the
  // on-record CONTACT assigned as reviewer, never a caller-supplied address.
  // entityId accepts a UUID string for entity_type 'rfi' — RFI.id is the
  // public_id (the backend resolves it to the internal numeric rfi_log.id).
  issue: (entityType: string, entityId: number | string, data?: { recipient_email?: string; recipient_name?: string; expires_in_days?: number }) =>
    request<{ token: DocumentToken; portal_url: string; email_status: string }>(
      `/api/v1/documents/${entityType}/${entityId}/issue`,
      { method: 'POST', ...body(data || {}) }
    ),
  listTokens: (entityType: string, entityId: number) =>
    request<{ data: DocumentToken[] }>(`/api/v1/documents/${entityType}/${entityId}/tokens`),
  revoke: (tokenId: string | number) =>
    request<{ status: string }>(`/api/v1/documents/tokens/${tokenId}/revoke`, { method: 'POST' }),
};


// ── Onboarding Checklist ──────────────────────────────────────────────────────

export interface ChecklistItem {
  item_key: string;
  label: string;
  status: 'pending' | 'complete' | 'dismissed';
  updated_at: string;
}

export interface ChecklistResponse {
  items: ChecklistItem[];
  total: number;
  done: number;
  all_done: boolean;
}

export const onboardingChecklist = {
  get: () => get<ChecklistResponse>('/onboarding/checklist'),
  update: (itemKey: string, status: 'pending' | 'complete' | 'dismissed') =>
    request<ChecklistItem>(`/api/v1/onboarding/checklist/${itemKey}`, { method: 'PATCH', ...body({ status }) }),
};



// ── User Assignments ──────────────────────────────────────────────────────────

export interface UserAssignment {
  id: string;
  company_id: number;
  user_id: number;
  contact_id?: number;
  user_name?: string;
  user_email?: string;
  assignment_type: 'location' | 'project';
  location_id?: number;
  project_id?: number;
  role: string;
  start_date?: string;
  end_date?: string;
  notes?: string;
  created_at: string;
  project_name?: string;
  location_name?: string;
}

// ── Locations ─────────────────────────────────────────────────────────────────

export interface AppLocation {
  id: number;
  name: string;
  parent_id?: number;
  is_public?: boolean;
  kiosk_token?: string;
  allow_complete?: boolean;
  warning_message?: string;
  // SPEC-LOCATION-KIOSK-REFRESH (#437) — Big-Board camera rail gates on this.
  // 'none' = no rail; 'frigate'/etc = render the conditional rail.
  camera_hosting_mode?: string;
  frigate_url?: string;
  // Whether cameras are turned on for this location. The kiosk camera rail
  // (#443) renders only when cameras_enabled && camera_hosting_mode != 'none'.
  cameras_enabled?: boolean;
  // Raw per-location camera config (jsonb). The kiosk reads the camera list
  // via the /cameras endpoint below rather than parsing this directly, but
  // it's surfaced here for callers that need the raw shape.
  camera_feeds?: unknown;
  created_at?: string;
  updated_at?: string;
}

// One camera as returned by GET /api/v1/locations/:id/cameras. The backend
// enriches each configured camera with proxy URLs so the client never needs
// the raw frigate_url / frigate_token (those stay server-side).
export interface LocationCamera {
  name: string;
  camera_id: string;
  snapshot_url: string;
  stream_url: string;
  events_url: string;
}

export const locationsApi = {
  list: () => request<AppLocation[]>('/api/v1/locations'),
  get: (id: number) => request<AppLocation>(`/api/v1/locations/${id}`),
  // Configured cameras for a location, each with proxy snapshot/stream/events
  // URLs. Auth-aware (accepts the kiosk viewer token). Used by the kiosk
  // camera rail (#443) + the Frigate dashboard widget.
  cameras: (id: number) => request<LocationCamera[]>(`/api/v1/locations/${id}/cameras`),
};

// ── FS Rooms (decision_folders as room picker source) ─────────────────────────
export interface FSRoomOption {
  folder_id: string;
  name: string;
  location_id?: number | null;
  finish_count?: number;
  open_decision_count?: number;
}

export const fsRoomsApi = {
  /**
   * List FS rooms (decision_folders) for a project — used by the task room picker.
   * Optional `q` runs a real server-side search instead of a client-side filter
   * (same scoped-search contract as `assignablePeople.list()` — #803 AC3).
   */
  listForProject: (projectId: string | number, q?: string) =>
    request<{ rooms: FSRoomOption[] }>(
      `/api/v1/projects/${projectId}/finish-schedule/rooms${q ? `?q=${encodeURIComponent(q)}` : ''}`
    ),
};

// ── Schedule Calendars (workday clock) ────────────────────────────────────────
// spec: specs/task-dependencies-workday.md

export interface WorkingTime {
  start: string; // "07:00"
  end: string;   // "17:00"
}

export interface WorkingTimes {
  mon: WorkingTime | null;
  tue: WorkingTime | null;
  wed: WorkingTime | null;
  thu: WorkingTime | null;
  fri: WorkingTime | null;
  sat: WorkingTime | null;
  sun: WorkingTime | null;
}

export interface NonWorkingDay {
  date: string;        // "YYYY-MM-DD" — start (or the single day)
  end_date?: string;   // "YYYY-MM-DD" — optional inclusive range end; omit/blank = single day
  recurring: boolean;  // annual recurrence (by month-day; ranges may wrap the year)
  name: string;
}

export interface ScheduleCalendar {
  id: number;
  company_id: number;
  name: string;
  is_default: boolean;
  working_times: WorkingTimes;
  non_working_days: NonWorkingDay[];
  workday_exceptions: NonWorkingDay[];
  created_at: string;
  updated_at: string;
}

export const scheduleCalendars = {
  list: () => request<ScheduleCalendar[]>('/api/v1/schedule-calendars'),
  getDefault: () => request<ScheduleCalendar>('/api/v1/schedule-calendars/default'),
  get: (id: number) => request<ScheduleCalendar>(`/api/v1/schedule-calendars/${id}`),
  create: (data: Partial<ScheduleCalendar>) =>
    request<ScheduleCalendar>('/api/v1/schedule-calendars', { method: 'POST', ...body(data) }),
  update: (id: number, data: Partial<ScheduleCalendar>) =>
    request<ScheduleCalendar>(`/api/v1/schedule-calendars/${id}`, { method: 'PATCH', ...body(data) }),
  delete: (id: number) =>
    request<void>(`/api/v1/schedule-calendars/${id}`, { method: 'DELETE' }),
  // Returns the projects in this company that are explicitly using the calendar.
  // Used to warn before saving a calendar edit that affects scheduled work.
  projectsUsing: (id: number) =>
    request<Array<{ id: number; name: string }>>(`/api/v1/schedule-calendars/${id}/projects`),
};

// ── User Assignments ──────────────────────────────────────────────────────────

export const userAssignments = {
  listForProject: (projectId: string) =>
    request<UserAssignment[]>(`/api/v1/projects/${projectId}/assignments`),
  addToProject: (projectId: string, data: { user_id: number; role: string; start_date?: string; end_date?: string; notes?: string }) =>
    request<UserAssignment>(`/api/v1/projects/${projectId}/assignments`, { method: 'POST', ...body(data) }),
  removeFromProject: (projectId: string, userId: number) =>
    request<void>(`/api/v1/projects/${projectId}/assignments/${userId}`, { method: 'DELETE' }),
  /** Edit ONE assignment segment's dates (by its own row id) — used by the
   * all-projects schedule assignments view where each segment is independent. */
  updateEntry: (projectId: number, assignmentId: number, data: { start_date?: string; end_date?: string; role?: string; notes?: string }) =>
    request<{ ok: boolean; id: number }>(`/api/v1/projects/${projectId}/assignments/entry/${assignmentId}`, { method: 'PATCH', ...body(data) }),
  /** Delete ONE assignment segment (by its own row id), not every row for the pair. */
  removeEntry: (projectId: number, assignmentId: number) =>
    request<void>(`/api/v1/projects/${projectId}/assignments/entry/${assignmentId}`, { method: 'DELETE' }),
  /** Bulk/group assign: a mix of employee contacts, individual users and/or
   * company Teams to a project for a date period. Assignments are contact-keyed —
   * a no-account employee (contact with no user) can be assigned and the row
   * resolves to them once they accept their invite. Teams expand to their member
   * contacts server-side. Returns the created assignment rows. */
  bulkAddToProject: (
    projectId: string,
    data: { contact_ids?: number[]; user_ids?: number[]; team_ids?: number[]; start_date: string; end_date: string; role?: string; notes?: string },
  ) =>
    request<UserAssignment[]>(`/api/v1/projects/${projectId}/assignments/bulk`, { method: 'POST', ...body(data) }),
  listForLocation: (locationId: number) =>
    request<UserAssignment[]>(`/api/v1/locations/${locationId}/assignments`),
  /** Provide exactly one of user_id / contact_id. contact_id assigns a
   * never-activated employee contact (#1879) — same contract as
   * bulkAddToProject/addToProject's contact-keyed path. */
  addToLocation: (locationId: number, data: { user_id?: number; contact_id?: number; role: string; start_date?: string; end_date?: string; notes?: string }) =>
    request<UserAssignment>(`/api/v1/locations/${locationId}/assignments`, { method: 'POST', ...body(data) }),
  /** id is the directory-row identity: positive = users.id, negative = -contacts.id
   * (no-account employee row) — same convention as removeFromProject. */
  removeFromLocation: (locationId: number, id: number) =>
    request<void>(`/api/v1/locations/${locationId}/assignments/${id}`, { method: 'DELETE' }),
  listForUser: (userId: number) =>
    request<UserAssignment[]>(`/api/v1/users/${userId}/assignments`),
};

// ── PDF Download Helper ───────────────────────────────────────────────────────

/**
 * downloadPDF fetches a PDF endpoint and triggers a browser download.
 * On native Capacitor (iOS/Android), writes to the device Documents directory
 * via @capacitor/filesystem so the file appears in the Files app.
 * @param path  API path relative to /api/v1 (e.g. '/bills/42/pdf')
 * @param filename  Suggested filename for the download
 */
export async function downloadPDF(path: string, filename: string): Promise<void> {
  const token = getToken();
  const fetchUrl = IS_CAPACITOR_NATIVE ? BASE_URL + '/api/v1' + path : '/api/v1' + path;
  const res = await fetch(fetchUrl, {
    headers: token ? { Authorization: 'Bearer ' + token } : {},
  });
  if (!res.ok) throw new Error('PDF generation failed: ' + res.status);
  const blob = await res.blob();

  if (IS_CAPACITOR_NATIVE) {
    // On native iOS/Android: write to Documents directory via @capacitor/filesystem
    // so the file is accessible in the device Files app.
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        // Strip "data:application/pdf;base64," prefix
        resolve(result.split(',')[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    await Filesystem.writeFile({
      path: filename,
      data: base64,
      directory: Directory.Documents,
    });
    return;
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}


// ── Profile (duplicate removed — UserProfile defined above) ───────────────────

// ── MFA / 2FA ─────────────────────────────────────────────────────────────────

export interface MFASetupResponse {
  secret: string;
  otp_url: string;
  qr_code_png: string;  // base64 PNG
  backup_codes: string[];
}

export interface MFAStatusResponse {
  enabled: boolean;
  backup_codes_remaining: number;
}

export interface MFALoginResponse {
  mfa_required: boolean;
  challenge_token: string;
}

export interface MFACompleteLoginResponse extends LoginResponse {
  trusted_device_token?: string;
}

export interface TrustedDevice {
  id: string;
  label: string;
  created_at: string;
  expires_at: string;
}

export const mfa = {
  status: () =>
    request<MFAStatusResponse>('/api/v1/me/mfa/status'),
  setup: () =>
    request<MFASetupResponse>('/api/v1/me/mfa/setup', { method: 'POST' }),
  verify: (code: string) =>
    request<{ enabled: boolean }>('/api/v1/me/mfa/verify', { method: 'POST', ...body({ code }) }),
  disable: () =>
    request<{ enabled: boolean }>('/api/v1/me/mfa', { method: 'DELETE' }),
  completeLogin: async (challenge_token: string, code: string, rememberDevice?: boolean): Promise<MFACompleteLoginResponse> => {
    const data = await request<MFACompleteLoginResponse>(
      '/api/v1/auth/mfa',
      { method: 'POST', ...body({ challenge_token, code, remember_device: !!rememberDevice }) },
    );
    if (data.refresh_token) await setRefreshToken(data.refresh_token);
    if (data.trusted_device_token) { localStorage.setItem('tenon_trusted_device', data.trusted_device_token); await _mirrorAuth(); }
    return data;
  },
  listTrustedDevices: () =>
    request<{ devices: TrustedDevice[] }>('/api/v1/me/mfa/trusted-devices'),
  revokeAllTrustedDevices: () =>
    request<{ revoked: boolean }>('/api/v1/me/mfa/trusted-devices', { method: 'DELETE' }),
};

export interface ProjectInboundEmailData {
  project_id: number
  bills_email?: string
  expenses_email?: string
}

export const projectInboundEmail = {
  get: (projectId: string) =>
    request<ProjectInboundEmailData>(`/api/v1/projects/${projectId}/inbound-email`),
  generate: (projectId: string) =>
    request<ProjectInboundEmailData>(`/api/v1/projects/${projectId}/inbound-email/generate`, { method: 'POST', ...body({}) }),
}

export interface NHTSARecall {
  campaign_number: string
  component: string
  summary: string
  consequence: string
  remedy: string
  report_date: string
  manufacturer: string
  park_it: boolean
}

export const nhtsa = {
  getMakes: (): Promise<{ makes: string[] }> =>
    get('/nhtsa/makes'),
  getModels: (make: string, year?: number): Promise<{ models: string[] }> =>
    get(`/nhtsa/models?make=${encodeURIComponent(make)}${year ? `&year=${year}` : ''}`),
  decodeVin: (vin: string): Promise<{ year: string; make: string; model: string; trim: string; fuel_type: string; error_description: string }> =>
    get(`/nhtsa/vin/${encodeURIComponent(vin)}`),
  getRecalls: (make: string, model: string, year: number): Promise<{ count: number; recalls: NHTSARecall[] }> =>
    get(`/nhtsa/recalls?make=${encodeURIComponent(make)}&model=${encodeURIComponent(model)}&year=${year}`),
}


// ── S7: License & Insurance Tracking ────────────────────────────────────────

export interface ComplianceDocument {
  id: number
  company_id: number
  contact_id: number | null
  contact_name?: string | null
  document_type: string
  document_number?: string | null
  issuer?: string | null
  coverage_amount?: number | null
  issued_date?: string | null
  expiry_date?: string | null
  status: 'active' | 'expiring_soon' | 'expired' | 'unknown'
  file_url?: string | null
  notes?: string | null
  alert_sent_60: boolean
  alert_sent_30: boolean
  alert_sent_15: boolean
  alert_sent_7: boolean
  created_by?: number | null
  created_at: string
  updated_at: string
  days_until_expiry?: number | null
}

export interface ComplianceDocumentRequest {
  document_type: string
  document_number?: string | null
  issuer?: string | null
  coverage_amount?: number | null
  issued_date?: string | null
  expiry_date?: string | null
  file_url?: string | null
  notes?: string | null
}

export interface ComplianceDashboard {
  expired: ComplianceDocument[]
  expiring_30: ComplianceDocument[]
  expiring_60: ComplianceDocument[]
  compliant: ComplianceDocument[]
  unknown: ComplianceDocument[]
}

export const compliance = {
  listForContact: (contactId: string) =>
    request<ComplianceDocument[]>(`/api/v1/contacts/${contactId}/compliance`),
  createForContact: (contactId: string, data: ComplianceDocumentRequest) =>
    request<ComplianceDocument>(`/api/v1/contacts/${contactId}/compliance`, { method: 'POST', body: JSON.stringify(data) }),
  updateForContact: (contactId: string, docId: number, data: ComplianceDocumentRequest) =>
    request<ComplianceDocument>(`/api/v1/contacts/${contactId}/compliance/${docId}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteForContact: (contactId: string, docId: number) =>
    request<void>(`/api/v1/contacts/${contactId}/compliance/${docId}`, { method: 'DELETE' }),

  listCompanyDocs: () =>
    request<ComplianceDocument[]>(`/api/v1/settings/compliance`),
  createCompanyDoc: (data: ComplianceDocumentRequest) =>
    request<ComplianceDocument>(`/api/v1/settings/compliance`, { method: 'POST', body: JSON.stringify(data) }),
  updateCompanyDoc: (docId: number, data: ComplianceDocumentRequest) =>
    request<ComplianceDocument>(`/api/v1/settings/compliance/${docId}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteCompanyDoc: (docId: number) =>
    request<void>(`/api/v1/settings/compliance/${docId}`, { method: 'DELETE' }),

  dashboard: () =>
    request<ComplianceDashboard>(`/api/v1/compliance/dashboard`),
}

// ── Drawings V2 (revision-tracked drawings) ──────────────────────────────────

export interface DrawingV2 {
  id: string;
  project_id: number;
  company_id: number;
  sheet_number: string;
  title: string;
  discipline: string;
  current_revision: string;
  drawing_set_id?: string | null;
  received_date?: string | null;
  created_by?: number | null;
  created_at: string;
  updated_at: string;
  revisions?: DrawingRevisionV2[];
}

export interface DrawingRevisionV2 {
  id: string;
  drawing_id: string;
  revision: string;
  revision_date: string;
  description: string;
  file_path: string;
  file_name: string;
  file_size: number;
  uploaded_by?: number | null;
  created_at: string;
}

export interface DrawingSetV2 {
  id: string;
  project_id: number;
  company_id: number;
  name: string;
  description: string;
  issue_date?: string | null;
  created_at: string;
  updated_at: string;
}

export const drawingsV2 = {
  list: (projectId: string, params?: { discipline?: string; set_id?: string }) => {
    const q = new URLSearchParams();
    if (params?.discipline) q.set("discipline", params.discipline);
    if (params?.set_id) q.set("set_id", params.set_id);
    return request<DrawingV2[]>(`/api/v1/projects/${projectId}/drawings/v2${q.toString() ? "?" + q : ""}`);
  },
  get: (id: string) => request<DrawingV2>(`/api/v1/drawings/v2/${id}`),
  create: (projectId: string, data: Record<string, unknown>) =>
    request<DrawingV2>(`/api/v1/projects/${projectId}/drawings/v2`, { method: "POST", ...body(data) }),
  update: (id: string, data: Record<string, unknown>) =>
    request<DrawingV2>(`/api/v1/drawings/v2/${id}`, { method: "PATCH", ...body(data) }),
  delete: (id: string) => request<void>(`/api/v1/drawings/v2/${id}`, { method: "DELETE" }),
  listRevisions: (id: string) => request<DrawingRevisionV2[]>(`/api/v1/drawings/v2/${id}/revisions`),
  addRevision: (id: string, data: Record<string, unknown>) =>
    request<DrawingRevisionV2>(`/api/v1/drawings/v2/${id}/revisions`, { method: "POST", ...body(data) }),
};

export const drawingSetsV2 = {
  list: (projectId: string) => request<DrawingSetV2[]>(`/api/v1/projects/${projectId}/drawing-sets`),
  create: (projectId: string, data: { name: string; description?: string; issue_date?: string }) =>
    request<DrawingSetV2>(`/api/v1/projects/${projectId}/drawing-sets`, { method: "POST", ...body(data) }),
  update: (id: string, data: { name?: string; description?: string; issue_date?: string }) =>
    request<void>(`/api/v1/drawing-sets/${id}`, { method: "PATCH", ...body(data) }),
  delete: (id: string) => request<void>(`/api/v1/drawing-sets/${id}`, { method: "DELETE" }),
  exportUrl: (id: string) => `/api/v1/drawing-sets/${id}/export`,
};

// ── SPEC-PROJECT-MEMORY (#91) D5 — Project Memory API ────────────────────────

export interface ProjectMemorySection {
  section_key: string
  content: string
  version: number
  updated_at: string
  updated_by_user_id?: number | null
  updated_by_agent_id?: string | null
  updated_by_name?: string | null
}

export type ProjectMemoryFeedEntryType = 'memory_write' | 'transcript' | 'comm' | 'action' | 'note'

export interface ProjectMemoryFeedEntry {
  id: number
  entry_type: ProjectMemoryFeedEntryType
  source_agent_id?: string | null
  source_user_id?: number | null
  section_key?: string | null
  payload: string
  created_at: string
  source_user_name?: string | null
}

export const MEMORY_SECTION_LABELS: Record<string, string> = {
  root:       'Summary',
  contacts:   'Contacts',
  schedule:   'Schedule',
  scope:      'Scope',
  sourcing:   'Sourcing',
  finance:    'Finance',
  comms:      'Comms',
  scratchpad: 'Scratchpad',
}

export const MEMORY_SECTION_KEYS = ['root','contacts','schedule','scope','sourcing','finance','comms','scratchpad'] as const

export const projectMemory = {
  /** GET /api/v1/projects/{id}/memory?sections=root,contacts,... */
  getSections: (projectId: string, sections: string[] = ['root']) =>
    request<{ sections: Record<string, ProjectMemorySection> }>(
      `/api/v1/projects/${projectId}/memory?sections=${sections.join(',')}`
    ),

  /** PUT /api/v1/projects/{id}/memory/{section_key} */
  updateSection: (projectId: string, sectionKey: string, content: string) =>
    request<ProjectMemorySection>(
      `/api/v1/projects/${projectId}/memory/${sectionKey}`,
      { method: 'PUT', ...body({ content }) }
    ),

  /** PATCH /api/v1/projects/{id}/memory/{section_key}/append */
  appendNote: (projectId: string, sectionKey: string, note: string) =>
    request<ProjectMemorySection>(
      `/api/v1/projects/${projectId}/memory/${sectionKey}/append`,
      { method: 'PATCH', ...body({ note }) }
    ),

  /** GET /api/v1/projects/{id}/feed?types=...&since=...&limit=... */
  getFeed: (projectId: string, params?: { types?: string; since?: string; limit?: number }) => {
    const q = new URLSearchParams()
    if (params?.types) q.set('types', params.types)
    if (params?.since) q.set('since', params.since)
    if (params?.limit) q.set('limit', String(params.limit))
    return request<{ entries: ProjectMemoryFeedEntry[]; total: number }>(
      `/api/v1/projects/${projectId}/feed${q.toString() ? '?' + q : ''}`
    )
  },

  /** GET /api/v1/projects/{id}/feed/search?q=...&types=...&limit=... */
  searchFeed: (projectId: string, q: string, params?: { types?: string; limit?: number }) => {
    const qs = new URLSearchParams({ q })
    if (params?.types) qs.set('types', params.types)
    if (params?.limit) qs.set('limit', String(params.limit))
    return request<{ entries: ProjectMemoryFeedEntry[]; total: number }>(
      `/api/v1/projects/${projectId}/feed/search?${qs}`
    )
  },

  /** POST /api/v1/projects/{id}/memory/{section_key}/regenerate */
  regenerateSection: (projectId: string, sectionKey: string) =>
    request<{ success: boolean; section_key: string; content: string }>(
      `/api/v1/projects/${projectId}/memory/${sectionKey}/regenerate`,
      { method: 'POST' }
    ),
};
